import { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { MainLayout } from './components/layout/MainLayout'
import { NotFoundPage } from './pages/NotFoundPage'
import { lazyWithRetry as lazy } from './lib/lazyWithRetry'
import { CABINET_SETTINGS_SECTIONS } from './config/cabinetSettingsSections'
import { aiExpertsRoutes } from './routes/ai-experts.routes'
import { eventsRoutes } from './routes/events.routes'

// Loading fallback
const Loading = () => (
  <div className="flex h-full items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
)

// Core pages
const SignupPage = lazy(() => import('./pages/SignupPage'))
// Public build/version page — UNAUTHENTICATED, reachable by opening /versionsbuilt.
const VersionsBuiltPage = lazy(() => import('./pages/VersionsBuiltPage'))
const CabinetDashboardPage = lazy(() => import('./pages/dashboard/CabinetDashboardPage'))
const HubPage = lazy(() => import('./pages/HubPage'))
const ClientsListPage = lazy(() => import('./pages/clients/ClientsListPage'))
const ClientCreatePage = lazy(() => import('./pages/clients/ClientCreatePage'))
const MandatsListPage = lazy(() => import('./pages/mandats/MandatsListPage'))
const AdsManagerPage = lazy(() => import('./pages/ads/AdsManagerPage'))
// COLLAB-03: Provider<->Client engagement planning (audit/assessment plans + milestones)
const PlanningPage = lazy(() => import('./pages/mandats/PlanningPage'))
// COLLAB-09: Engagement progress aggregation + status reports
const EngagementProgressPage = lazy(() => import('./pages/mandats/EngagementProgressPage'))
// COLLAB-11: Unified engagement hub + lifecycle timeline
const EngagementHubPage = lazy(() => import('./pages/mandats/EngagementHubPage'))
// COLLAB-01: Provider<->Client messaging
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage'))
// SOCIAL-LAYER (Feed) — global social network feed (governance-service)
const SocialFeedPage = lazy(() => import('./pages/feed/SocialFeedPage').then((m) => ({ default: m.SocialFeedPage })))
// SOCIAL-LAYER (Forum) — business-owned discussion forums (workspace-service)
const ForumListPage = lazy(() => import('./pages/forum').then((m) => ({ default: m.ForumListPage })))
const ForumBoardPage = lazy(() => import('./pages/forum').then((m) => ({ default: m.ForumBoardPage })))
const ForumTopicPage = lazy(() => import('./pages/forum').then((m) => ({ default: m.TopicPage })))
const ForumAdminPage = lazy(() => import('./pages/forum').then((m) => ({ default: m.ForumAdminPage })))
// SOCIAL-LAYER (Booking) — availability config + received-bookings inbox (client-management-service)
const BookingSettingsPage = lazy(() => import('./pages/booking/BookingSettingsPage').then((m) => ({ default: m.BookingSettingsPage })))

// Governance Pro
const GovernanceHubPage = lazy(() => import('./pages/governance-pro/HubPage'))
const GovernanceCockpitPage = lazy(() => import('./pages/governance-pro/CockpitPage'))
const GovernanceWizardPage = lazy(() => import('./pages/governance-pro/WizardPage'))
const GovernanceFrameworkPage = lazy(() => import('./pages/governance-pro/FrameworkPage'))
const GovernanceStandardsPage = lazy(() => import('./pages/governance-pro/StandardsPage'))
const GovernanceModelPage = lazy(() => import('./pages/governance-pro/ModelPage'))
const GovernanceEncadrementPage = lazy(() => import('./pages/governance-pro/EncadrementPage'))
const GovernanceGRCDashboardPage = lazy(() => import('./pages/governance-pro/GRCDashboardPage'))
const GovernanceRoadmapPage = lazy(() => import('./pages/governance-pro/RoadmapPage'))

// Security Pro
const SecurityHubPage = lazy(() => import('./pages/security-pro/HubPage'))
const SecurityCockpitPage = lazy(() => import('./pages/security-pro/CockpitPage'))
const SecurityScorePage = lazy(() => import('./pages/security-pro/ScorePage'))
const SecurityPosturePage = lazy(() => import('./pages/security-pro/PosturePage'))
const SecurityAlertsPage = lazy(() => import('./pages/security-pro/AlertsPage'))
const SecurityIncidentsPage = lazy(() => import('./pages/security-pro/IncidentsPage'))
const SecurityThreatIntelPage = lazy(() => import('./pages/security-pro/ThreatIntelPage'))
const SecurityVulnsPage = lazy(() => import('./pages/security-pro/VulnsPage'))
const SecurityControlsPage = lazy(() => import('./pages/security-pro/ControlsPage'))
const SecurityBcpPage = lazy(() => import('./pages/security-pro/BcpPage'))
const SecurityDrPage = lazy(() => import('./pages/security-pro/DrPage'))
const SecurityIso27001Page = lazy(() => import('./pages/security-pro/Iso27001Page'))
const SecuritySoc2Page = lazy(() => import('./pages/security-pro/Soc2Page'))
const SecurityFrameworksPage = lazy(() => import('./pages/security-pro/FrameworksPage'))
const SecurityTemplatesPage = lazy(() => import('./pages/security-pro/TemplatesPage'))
const SecurityExercisesPage = lazy(() => import('./pages/security-pro/ExercisesPage'))
const SecurityReportExecPage = lazy(() => import('./pages/security-pro/ReportExecPage'))
const SecurityReportOpsPage = lazy(() => import('./pages/security-pro/ReportOpsPage'))
const SecurityAuditTrailPage = lazy(() => import('./pages/security-pro/AuditTrailPage'))
const SecurityIntegrationPage = lazy(() => import('./pages/security-pro/IntegrationPage'))
const SecuritySettingsPage = lazy(() => import('./pages/security-pro/SettingsPage'))
const SecurityThreatIntelFeedPage = lazy(() => import('./pages/security-pro/ThreatIntelFeedPage'))
const SecurityPentestParserPage = lazy(() => import('./pages/security-pro/PentestParserPage'))
const SecurityBcpDrSimulatorPage = lazy(() => import('./pages/security-pro/BcpDrSimulatorPage'))
const SecuritySocMaturityPage = lazy(() => import('./pages/security-pro/SocMaturityPage'))
const SecurityComplianceControlLinkerPage = lazy(() => import('./pages/security-pro/ComplianceControlLinkerPage'))

// Risk Pro
const RiskHubPage = lazy(() => import('./pages/risk-pro/HubPage'))
const RiskRegisterPage = lazy(() => import('./pages/risk-pro/RegisterPage'))
const RiskIdentifyWizardPage = lazy(() => import('./pages/risk-pro/IdentifyWizardPage'))
const RiskScenariosPage = lazy(() => import('./pages/risk-pro/ScenariosPage'))
const RiskBowtiePage = lazy(() => import('./pages/risk-pro/BowtiePage'))
const RiskControlsPage = lazy(() => import('./pages/risk-pro/ControlsPage'))
const RiskTreatmentPage = lazy(() => import('./pages/risk-pro/TreatmentPage'))
const RiskKriPage = lazy(() => import('./pages/risk-pro/KriPage'))
const RiskIncidentsPage = lazy(() => import('./pages/risk-pro/IncidentsPage'))
const RiskTaxonomyPage = lazy(() => import('./pages/risk-pro/TaxonomyPage'))
const RiskTemplatesPage = lazy(() => import('./pages/risk-pro/TemplatesPage'))
const RiskFrameworksPage = lazy(() => import('./pages/risk-pro/FrameworksPage'))
const RiskReportBoardPage = lazy(() => import('./pages/risk-pro/ReportBoardPage'))
const RiskSettingsPage = lazy(() => import('./pages/risk-pro/SettingsPage'))

// Data Pro
const DataHubPage = lazy(() => import('./pages/data-pro/HubPage'))
const DataCockpitPage = lazy(() => import('./pages/data-pro/CockpitPage'))
const DataScorePage = lazy(() => import('./pages/data-pro/ScorePage'))
const DataGovernancePage = lazy(() => import('./pages/data-pro/DataGovernancePage'))
const DataQualityPage = lazy(() => import('./pages/data-pro/DataQualityPage'))
const DataMDMPage = lazy(() => import('./pages/data-pro/MDMPage'))
const DataArchitecturePage = lazy(() => import('./pages/data-pro/ArchitecturePage'))
const DataMetadataPage = lazy(() => import('./pages/data-pro/MetadataPage'))
const DataModelingPage = lazy(() => import('./pages/data-pro/ModelingPage'))
const DataStoragePage = lazy(() => import('./pages/data-pro/StoragePage'))
const DataSecurityPage = lazy(() => import('./pages/data-pro/SecurityPage'))
const DataLifecyclePage = lazy(() => import('./pages/data-pro/LifecyclePage'))
const DataIntegrationPage = lazy(() => import('./pages/data-pro/IntegrationPipelinesPage'))
const DataDwhBiPage = lazy(() => import('./pages/data-pro/DwhBiPage'))
const DataDocumentsPage = lazy(() => import('./pages/data-pro/DocumentsPage'))
const DataDcamPage = lazy(() => import('./pages/data-pro/DcamPage'))
const DataCdmcPage = lazy(() => import('./pages/data-pro/CdmcPage'))
const DataMaturitePage = lazy(() => import('./pages/data-pro/MaturitePage'))
const DataFrameworksPage = lazy(() => import('./pages/data-pro/FrameworksPage'))
const DataReportExecPage = lazy(() => import('./pages/data-pro/ReportExecPage'))
const DataSettingsPage = lazy(() => import('./pages/data-pro/SettingsPage'))

// Audit Pro
const AuditHubPage = lazy(() => import('./pages/audit-pro/HubPage'))
const AuditCockpitPage = lazy(() => import('./pages/audit-pro/CockpitPage'))
const AuditCalendarPage = lazy(() => import('./pages/audit-pro/CalendarPage'))
const AuditAnnualPlanPage = lazy(() => import('./pages/audit-pro/AnnualPlanPage'))
const AuditPlanWizardPage = lazy(() => import('./pages/audit-pro/PlanWizardPage'))
const AuditUniversePage = lazy(() => import('./pages/audit-pro/UniversePage'))
const AuditMissionsPage = lazy(() => import('./pages/audit-pro/MissionsPage'))
const AuditFindingsPage = lazy(() => import('./pages/audit-pro/FindingsPage'))
const AuditActionsPage = lazy(() => import('./pages/audit-pro/ActionsPage'))
const AuditTrackingPage = lazy(() => import('./pages/audit-pro/TrackingPage'))
const AuditSelfAssessPage = lazy(() => import('./pages/audit-pro/SelfAssessPage'))
const AuditReportPage = lazy(() => import('./pages/audit-pro/ReportAuditPage'))
const AuditCertDashboardPage = lazy(() => import('./pages/audit-pro/CertDashboardPage'))
const AuditCertGapPage = lazy(() => import('./pages/audit-pro/CertGapPage'))
const AuditCertReadinessPage = lazy(() => import('./pages/audit-pro/CertReadinessPage'))
const AuditCertTimelinePage = lazy(() => import('./pages/audit-pro/CertTimelinePage'))
const AuditSettingsPage = lazy(() => import('./pages/audit-pro/SettingsPage'))
const AuditReportNarratorPage = lazy(() => import('./pages/audit-pro/ReportNarratorPage'))
const AuditFollowUpTrackerAIPage = lazy(() => import('./pages/audit-pro/FollowUpTrackerAIPage'))
const AuditUniverseAIPage = lazy(() => import('./pages/audit-pro/AuditUniverseAIPage'))
const AuditWorkpaperGeneratorPage = lazy(() => import('./pages/audit-pro/WorkpaperGeneratorPage'))
const AuditFindingClassifierPage = lazy(() => import('./pages/audit-pro/FindingClassifierPage'))
// Wave1 audit-pro fix: register all 27 page files that previously had no <Route>
const AuditEngagementPage = lazy(() => import('./pages/audit-pro/EngagementPage'))
const AuditFieldworkPage = lazy(() => import('./pages/audit-pro/FieldworkPage'))
const AuditEvidencePage = lazy(() => import('./pages/audit-pro/EvidencePage'))
const AuditInterviewsPage = lazy(() => import('./pages/audit-pro/InterviewsPage'))
const AuditWorkpapersPage = lazy(() => import('./pages/audit-pro/WorkpapersPage'))
const AuditRecommendationsPage = lazy(() => import('./pages/audit-pro/RecommendationsPage'))
const AuditQualityReviewPage = lazy(() => import('./pages/audit-pro/QualityReviewPage'))
const AuditMethodologyPage = lazy(() => import('./pages/audit-pro/MethodologyPage'))
const AuditTemplatesPage = lazy(() => import('./pages/audit-pro/TemplatesPage'))
const AuditChecklistsPage = lazy(() => import('./pages/audit-pro/ChecklistsPage'))
const AuditResourcesPage = lazy(() => import('./pages/audit-pro/ResourcesPage'))
const AuditAuditTrailPage = lazy(() => import('./pages/audit-pro/AuditTrailPage'))
const AuditCommitteePage = lazy(() => import('./pages/audit-pro/CommitteePage'))
const AuditCertAuditWorkspacePage = lazy(() => import('./pages/audit-pro/CertAuditWorkspacePage'))
const AuditCertBodyPortalPage = lazy(() => import('./pages/audit-pro/CertBodyPortalPage'))
const AuditCertBodyRegistryPage = lazy(() => import('./pages/audit-pro/CertBodyRegistryPage'))
const AuditCertControlsPage = lazy(() => import('./pages/audit-pro/CertControlsPage'))
const AuditCertDecisionPage = lazy(() => import('./pages/audit-pro/CertDecisionPage'))
const AuditCertDocRequirementsPage = lazy(() => import('./pages/audit-pro/CertDocRequirementsPage'))
const AuditCertEvidenceRequestsPage = lazy(() => import('./pages/audit-pro/CertEvidenceRequestsPage'))
const AuditCertProjectsPage = lazy(() => import('./pages/audit-pro/CertProjectsPage'))
const AuditCertProjectWizardPage = lazy(() => import('./pages/audit-pro/CertProjectWizardPage'))
const AuditCertSurveillancePage = lazy(() => import('./pages/audit-pro/CertSurveillancePage'))
const AuditClientPortalPage = lazy(() => import('./pages/audit-pro/ClientPortalPage'))
const AuditIntegrationPage = lazy(() => import('./pages/audit-pro/IntegrationPage'))
const AuditOutilsPage = lazy(() => import('./pages/audit-pro/OutilsPage'))
// Wave3 S10 fix: register 10 missing audit-pro pages (stubs with create-dialog)
const AuditCertNonconformitiesPage = lazy(() => import('./pages/audit-pro/CertNonconformitiesPage'))
const AuditContinuousAuditMonitorPage = lazy(() => import('./pages/audit-pro/ContinuousAuditMonitorPage'))
const AuditStatisticalSamplingPage = lazy(() => import('./pages/audit-pro/StatisticalSamplingPage'))
const AuditFraudPatternDetectorPage = lazy(() => import('./pages/audit-pro/FraudPatternDetectorPage'))
const AuditEvidenceBlockchainPage = lazy(() => import('./pages/audit-pro/EvidenceBlockchainPage'))
const AuditCaeDashboardPage = lazy(() => import('./pages/audit-pro/CaeDashboardPage'))
const AuditCertSchedulingPage = lazy(() => import('./pages/audit-pro/CertSchedulingPage'))
const AuditCertCertificatesPage = lazy(() => import('./pages/audit-pro/CertCertificatesPage'))
const AuditCertScopeAdjustPage = lazy(() => import('./pages/audit-pro/CertScopeAdjustPage'))
const AuditCertProjectWizardAltPage = lazy(() => import('./pages/audit-pro/CertProjectWizardAltPage'))

// AI/IA Pro
const AiIaHubPage = lazy(() => import('./pages/ai-ia-pro/HubPage'))
const AiIaCockpitPage = lazy(() => import('./pages/ai-ia-pro/CockpitPage'))
const AiIaScorePage = lazy(() => import('./pages/ai-ia-pro/ScorePage'))
const AiIaRegistrePage = lazy(() => import('./pages/ai-ia-pro/RegistrePage'))
const AiIaBiasPage = lazy(() => import('./pages/ai-ia-pro/BiasPage'))
const AiIaEUAIActPage = lazy(() => import('./pages/ai-ia-pro/EUAIActPage'))
const AiIaIso42001Page = lazy(() => import('./pages/ai-ia-pro/Iso42001Page'))
const AiIaNistAIPage = lazy(() => import('./pages/ai-ia-pro/NistAIPage'))
const AiIaImpactPage = lazy(() => import('./pages/ai-ia-pro/ImpactPage'))
const AiIaReadinessPage = lazy(() => import('./pages/ai-ia-pro/ReadinessPage'))
const AiIaHouseAssessmentPage = lazy(() => import('./pages/ai-ia-pro/HouseAssessmentPage'))
const AiIaMlRegistryPage = lazy(() => import('./pages/ai-ia-pro/MlRegistryPage'))
const AiIaRiskClassPage = lazy(() => import('./pages/ai-ia-pro/RiskClassPage'))
const AiIaPromptGovPage = lazy(() => import('./pages/ai-ia-pro/PromptGovPage'))
const AiIaFrameworksPage = lazy(() => import('./pages/ai-ia-pro/FrameworksPage'))
const AiIaAuditTrailPage = lazy(() => import('./pages/ai-ia-pro/AuditTrailPage'))
const AiIaIntegrationPage = lazy(() => import('./pages/ai-ia-pro/IntegrationPage'))
const AiIaReportExecPage = lazy(() => import('./pages/ai-ia-pro/ReportExecPage'))
const AiIaSettingsPage = lazy(() => import('./pages/ai-ia-pro/SettingsPage'))
const AiIaDataLineageDiscoveryPage = lazy(() => import('./pages/ai-ia-pro/DataLineageDiscoveryPage'))
const AiIaDocumentIntelligencePage = lazy(() => import('./pages/ai-ia-pro/DocumentIntelligencePage'))
const AiIaAIModelRiskScorerPage = lazy(() => import('./pages/ai-ia-pro/AIModelRiskScorerPage'))
const AiIaShadowAIPage = lazy(() => import('./pages/ai-ia-pro/ShadowAIPage'))
const AiIaDPIAAutoGeneratorPage = lazy(() => import('./pages/ai-ia-pro/DPIAAutoGeneratorPage'))
const AiIaDataQualityDashboardPage = lazy(() => import('./pages/ai-ia-pro/DataQualityDashboardPage'))
const AiIaDataGovAIPage = lazy(() => import('./pages/ai-ia-pro/DataGovAIPage'))
const AiIaExplainPage = lazy(() => import('./pages/ai-ia-pro/ExplainPage'))
const AiIaGenAIPoliciesPage = lazy(() => import('./pages/ai-ia-pro/GenAIPoliciesPage'))
const AiIaLifecyclePage = lazy(() => import('./pages/ai-ia-pro/LifecyclePage'))
const AiIaMLOpsPage = lazy(() => import('./pages/ai-ia-pro/MLOpsPage'))
const AiIaOutilsPage = lazy(() => import('./pages/ai-ia-pro/OutilsPage'))
const AiIaPerformancePage = lazy(() => import('./pages/ai-ia-pro/PerformancePage'))

// AG-05: Multi-agentic Console (agents catalog + agentic tasks + HITL)
const AgentConsolePage = lazy(() => import('./pages/agents-pro/AgentConsolePage'))

// Change Pro
const ChangeHubPage = lazy(() => import('./pages/change-pro/HubPage'))
const ChangeCockpitPage = lazy(() => import('./pages/change-pro/CockpitPage'))
const ChangeAdkarPage = lazy(() => import('./pages/change-pro/AdkarPage'))
const ChangeKotterPage = lazy(() => import('./pages/change-pro/KotterPage'))
const ChangeLewinBridgesPage = lazy(() => import('./pages/change-pro/LewinBridgesPage'))
const ChangeMcKinsey7sPage = lazy(() => import('./pages/change-pro/McKinsey7sPage'))
const ChangeRoadmapPage = lazy(() => import('./pages/change-pro/RoadmapPage'))
const ChangeChampionsPage = lazy(() => import('./pages/change-pro/ChampionsPage'))
const ChangeAdoptionPage = lazy(() => import('./pages/change-pro/AdoptionPage'))
const ChangeResistancePage = lazy(() => import('./pages/change-pro/ResistancePage'))
const ChangeCommunicationPage = lazy(() => import('./pages/change-pro/CommunicationPage'))
const ChangeFormationPage = lazy(() => import('./pages/change-pro/FormationPage'))
const ChangeImpactPage = lazy(() => import('./pages/change-pro/ImpactPage'))
const ChangeCulturePage = lazy(() => import('./pages/change-pro/CulturePage'))
const ChangePortfolioPage = lazy(() => import('./pages/change-pro/PortfolioPage'))
const ChangeEnpsPage = lazy(() => import('./pages/change-pro/EnpsPage'))
const ChangeRoiPage = lazy(() => import('./pages/change-pro/RoiPage'))
const ChangeMaturitePage = lazy(() => import('./pages/change-pro/MaturitePage'))
const ChangeAuditTrailPage = lazy(() => import('./pages/change-pro/AuditTrailPage'))
const ChangeIntegrationPage = lazy(() => import('./pages/change-pro/IntegrationPage'))
const ChangeReportExecPage = lazy(() => import('./pages/change-pro/ReportExecPage'))
const ChangeSettingsPage = lazy(() => import('./pages/change-pro/SettingsPage'))
const ChangeTrainingContentGenPage = lazy(() => import('./pages/change-pro/TrainingContentGenPage'))
const ChangeAdoptionPredictorPage = lazy(() => import('./pages/change-pro/AdoptionPredictorPage'))
const ChangeMicroLearningPage = lazy(() => import('./pages/change-pro/MicroLearningPage'))
// Wave 3 Shard 6c — register existing-but-unrouted change-pro pages
const ChangeScorePage = lazy(() => import('./pages/change-pro/ScorePage'))
const ChangeReadinessPage = lazy(() => import('./pages/change-pro/ReadinessPage'))
const ChangeOutilsPage = lazy(() => import('./pages/change-pro/OutilsPage'))

// Business Model Pro
const BmHubPage = lazy(() => import('./pages/business-model-pro/HubPage'))
const BmCockpitPage = lazy(() => import('./pages/business-model-pro/CockpitPage'))
const BmBusinessModelPage = lazy(() => import('./pages/business-model-pro/BusinessModelPage'))
const BmLeanCanvasPage = lazy(() => import('./pages/business-model-pro/LeanCanvasPage'))
const BmValuePropositionPage = lazy(() => import('./pages/business-model-pro/ValuePropositionPage'))
const BmSessionsPage = lazy(() => import('./pages/business-model-pro/SessionsPage'))
const BmScorePage = lazy(() => import('./pages/business-model-pro/ScorePage'))
const BmCanvasLibraryPage = lazy(() => import('./pages/business-model-pro/CanvasLibraryPage'))
const BmCustomCanvasPage = lazy(() => import('./pages/business-model-pro/CustomCanvasPage'))
const BmGuidedQuestionsPage = lazy(() => import('./pages/business-model-pro/GuidedQuestionsPage'))
const BmStrategicAlignmentPage = lazy(() => import('./pages/business-model-pro/StrategicAlignmentPage'))
const BmImpactAnalysisPage = lazy(() => import('./pages/business-model-pro/ImpactAnalysisPage'))
const BmBenchmarkingPage = lazy(() => import('./pages/business-model-pro/BenchmarkingPage'))
const BmContributionsPage = lazy(() => import('./pages/business-model-pro/ContributionsPage'))
const BmPlenaryPage = lazy(() => import('./pages/business-model-pro/PlenaryPage'))
const BmConsolidationPage = lazy(() => import('./pages/business-model-pro/ConsolidationPage'))
const BmCertificationPage = lazy(() => import('./pages/business-model-pro/CertificationPage'))
const BmMaturitePage = lazy(() => import('./pages/business-model-pro/MaturitePage'))
const BmFrameworksPage = lazy(() => import('./pages/business-model-pro/FrameworksPage'))
const BmAiProjectPage = lazy(() => import('./pages/business-model-pro/AiProjectPage'))
const BmAuditTrailPage = lazy(() => import('./pages/business-model-pro/AuditTrailPage'))
const BmIntegrationsPage = lazy(() => import('./pages/business-model-pro/IntegrationsPage'))
const BmReportExecPage = lazy(() => import('./pages/business-model-pro/ReportExecPage'))
const BmOutilsPage = lazy(() => import('./pages/business-model-pro/OutilsPage'))
const BmSettingsPage = lazy(() => import('./pages/business-model-pro/SettingsPage'))

// Maturity Pro
const MaturityProHubPage = lazy(() => import('./pages/maturity-pro/HubPage'))
const MaturityProCockpitPage = lazy(() => import('./pages/maturity-pro/CockpitPage'))
const MaturityProQuestionnairesPage = lazy(() => import('./pages/maturity-pro/QuestionnairesPage'))
const MaturityProBenchmarkingPage = lazy(() => import('./pages/maturity-pro/BenchmarkingPage'))
const MaturityProGapAnalysePage = lazy(() => import('./pages/maturity-pro/GapAnalysePage'))
const MaturityProProgressionPage = lazy(() => import('./pages/maturity-pro/ProgressionPage'))
const MaturityProObjectifsPage = lazy(() => import('./pages/maturity-pro/ObjectifsPage'))
const MaturityProScorePage = lazy(() => import('./pages/maturity-pro/ScorePage'))
const MaturityProReportExecPage = lazy(() => import('./pages/maturity-pro/ReportExecPage'))
const MaturityProSettingsPage = lazy(() => import('./pages/maturity-pro/SettingsPage'))
// Wave 3 Shard 6 — register existing-but-unrouted maturity-pro pages
const MaturityProCmmiPage = lazy(() => import('./pages/maturity-pro/CmmiPage'))
const MaturityProCobitPage = lazy(() => import('./pages/maturity-pro/CobitPage'))
const MaturityProIso33001Page = lazy(() => import('./pages/maturity-pro/Iso33001Page'))
const MaturityProFrameworksPage = lazy(() => import('./pages/maturity-pro/FrameworksPage'))
const MaturityProCustomModelPage = lazy(() => import('./pages/maturity-pro/CustomModelPage'))
const MaturityProDimensionsPage = lazy(() => import('./pages/maturity-pro/DimensionsPage'))
const MaturityProMappingPage = lazy(() => import('./pages/maturity-pro/MappingPage'))
const MaturityProMaturitePage = lazy(() => import('./pages/maturity-pro/MaturitePage'))
const MaturityProRoadmapPage = lazy(() => import('./pages/maturity-pro/RoadmapPage'))
const MaturityProCampaignsPage = lazy(() => import('./pages/maturity-pro/CampaignsPage'))
const MaturityProBenchmarksPage = lazy(() => import('./pages/maturity-pro/BenchmarksPage'))
const MaturityProAuditTrailPage = lazy(() => import('./pages/maturity-pro/AuditTrailPage'))
const MaturityProCertificationPage = lazy(() => import('./pages/maturity-pro/CertificationPage'))
const MaturityProIntegrationPage = lazy(() => import('./pages/maturity-pro/IntegrationPage'))
const MaturityProOutilsPage = lazy(() => import('./pages/maturity-pro/OutilsPage'))
const DcmmAssessmentsPage = lazy(() => import('./pages/maturity-pro/DcmmAssessmentsPage'))
const DcmmDetailPage = lazy(() => import('./pages/maturity-pro/DcmmDetailPage'))
const ClassificationPage = lazy(() => import('./pages/data-pro/ClassificationPage'))
const ADKARAssessmentPage = lazy(() => import('./pages/change-pro/ADKARAssessmentPage'))
const AIBiasAssessmentPage = lazy(() => import('./pages/ai-ia-pro/AIBiasAssessmentPage'))
const AIImpactAssessmentPage = lazy(() => import('./pages/ai-ia-pro/AIImpactAssessmentPage'))
const AIReadinessPage = lazy(() => import('./pages/data-pro/AIReadinessPage'))

// Compliance Pro
const CompProHubPage = lazy(() => import('./pages/compliance-pro/HubPage'))
const CompProCockpitPage = lazy(() => import('./pages/compliance-pro/CockpitPage'))
const CompProScorePage = lazy(() => import('./pages/compliance-pro/ScorePage'))
const CompProRgpdPage = lazy(() => import('./pages/compliance-pro/RgpdPage'))
const CompProLoi25Page = lazy(() => import('./pages/compliance-pro/Loi25Page'))
const CompProCcpaPage = lazy(() => import('./pages/compliance-pro/CcpaPage'))
const CompProPciDssPage = lazy(() => import('./pages/compliance-pro/PciDssPage'))
const CompProSoxPage = lazy(() => import('./pages/compliance-pro/SoxPage'))
const CompProIso27701Page = lazy(() => import('./pages/compliance-pro/Iso27701Page'))
const CompProNistPrivacyPage = lazy(() => import('./pages/compliance-pro/NistPrivacyPage'))
const CompProRegisterPage = lazy(() => import('./pages/compliance-pro/RegisterPage'))
const CompProActionsPage = lazy(() => import('./pages/compliance-pro/ActionsPage'))
const CompProGapAnalysisPage = lazy(() => import('./pages/compliance-pro/GapAnalysisPage'))
const CompProPosturePage = lazy(() => import('./pages/compliance-pro/PosturePage'))
const CompProConsentPage = lazy(() => import('./pages/compliance-pro/ConsentPage'))
const CompProDsrPage = lazy(() => import('./pages/compliance-pro/DsrPage'))
const CompProPiaPage = lazy(() => import('./pages/compliance-pro/PiaPage'))
const CompProTransfersPage = lazy(() => import('./pages/compliance-pro/TransfersPage'))
const CompProCertificationPage = lazy(() => import('./pages/compliance-pro/CertificationPage'))
const CompProFrameworksPage = lazy(() => import('./pages/compliance-pro/FrameworksPage'))
const CompProOutilsPage = lazy(() => import('./pages/compliance-pro/OutilsPage'))
// Copilot v3 — Framework gap + Questionnaire IA
const CompProFrameworkGapPage = lazy(() => import('./pages/compliance-pro/FrameworkGapPage'))
const CompProQuestionnaireAIPage = lazy(() => import('./pages/compliance-pro/QuestionnaireAIPage'))
const CompProReportExecPage = lazy(() => import('./pages/compliance-pro/ReportExecPage'))
const CompProAuditTrailPage = lazy(() => import('./pages/compliance-pro/AuditTrailPage'))
const CompProIntegrationPage = lazy(() => import('./pages/compliance-pro/IntegrationPage'))
const CompProSettingsPage = lazy(() => import('./pages/compliance-pro/SettingsPage'))

// Vendor Pro
const VendorProHubPage = lazy(() => import('./pages/vendor-pro/HubPage'))
const VendorProCockpitPage = lazy(() => import('./pages/vendor-pro/CockpitPage'))
const VendorProScorePage = lazy(() => import('./pages/vendor-pro/ScorePage'))
const VendorProCataloguePage = lazy(() => import('./pages/vendor-pro/CataloguePage'))
const VendorProContractsPage = lazy(() => import('./pages/vendor-pro/ContractsPage'))
const VendorProCostsPage = lazy(() => import('./pages/vendor-pro/CostsPage'))
const VendorProDueDiligencePage = lazy(() => import('./pages/vendor-pro/DueDiligencePage'))
const VendorProPerformancePage = lazy(() => import('./pages/vendor-pro/PerformancePage'))
const VendorProScorecardsPage = lazy(() => import('./pages/vendor-pro/ScorecardsPage'))
const VendorProSlaMonitoringPage = lazy(() => import('./pages/vendor-pro/SlaMonitoringPage'))
const VendorProConcentrationPage = lazy(() => import('./pages/vendor-pro/ConcentrationPage'))
const VendorProRenewalsPage = lazy(() => import('./pages/vendor-pro/RenewalsPage'))
const VendorProClausesPage = lazy(() => import('./pages/vendor-pro/ClausesPage'))
const VendorProSubprocessorsPage = lazy(() => import('./pages/vendor-pro/SubprocessorsPage'))
const VendorProRgpdPage = lazy(() => import('./pages/vendor-pro/RgpdPage'))
const VendorProMaturitePage = lazy(() => import('./pages/vendor-pro/MaturitePage'))
const VendorProTprmFrameworkPage = lazy(() => import('./pages/vendor-pro/TprmFrameworkPage'))
const VendorProCertificationsPage = lazy(() => import('./pages/vendor-pro/CertificationsPage'))
const VendorProVendorAuditPage = lazy(() => import('./pages/vendor-pro/VendorAuditPage'))
const VendorProBenchmarksPage = lazy(() => import('./pages/vendor-pro/BenchmarksPage'))
const VendorProOutilsPage = lazy(() => import('./pages/vendor-pro/OutilsPage'))
const VendorProReportExecPage = lazy(() => import('./pages/vendor-pro/ReportExecPage'))
const VendorProAuditTrailPage = lazy(() => import('./pages/vendor-pro/AuditTrailPage'))
const VendorProIntegrationsPage = lazy(() => import('./pages/vendor-pro/IntegrationsPage'))
const VendorProSettingsPage = lazy(() => import('./pages/vendor-pro/SettingsPage'))
const VendorProAttackSurfacePage = lazy(() => import('./pages/vendor-pro/AttackSurfacePage'))

// Évaluation & 360° — cross-actor evaluation (evaluate consultants/auditors/
// providers/missions + be evaluated by clients). Wired to evaluation-service.
const EvaluationHubPage = lazy(() => import('./pages/evaluation-pro/HubPage'))
const EvaluationCriteriaPage = lazy(() => import('./pages/evaluation-pro/CriteriaPage'))
const EvaluationCyclesPage = lazy(() => import('./pages/evaluation-pro/CyclesPage'))
const EvaluationEvaluationsPage = lazy(() => import('./pages/evaluation-pro/EvaluationsPage'))
const EvaluationDetailPage = lazy(() => import('./pages/evaluation-pro/EvaluationDetailPage'))
const EvaluationScorecardsPage = lazy(() => import('./pages/evaluation-pro/ScorecardsPage'))
const EvaluationAnalyticsPage = lazy(() => import('./pages/evaluation-pro/AnalyticsPage'))
const EvaluationActionsPage = lazy(() => import('./pages/evaluation-pro/ActionsPage'))

// Dashboard Pro
const DashProHubPage = lazy(() => import('./pages/dashboard-pro/HubPage'))
const DashProScorePage = lazy(() => import('./pages/dashboard-pro/ScorePage'))
const DashProPanoramaPage = lazy(() => import('./pages/dashboard-pro/PanoramaPage'))
const DashProKpisPage = lazy(() => import('./pages/dashboard-pro/KpisPage'))
const DashProScoreConsolidePage = lazy(() => import('./pages/dashboard-pro/ScoreConsolidePage'))
const DashProMaturiteGlobalePage = lazy(() => import('./pages/dashboard-pro/MaturiteGlobalePage'))
const DashProRisqueAgregePage = lazy(() => import('./pages/dashboard-pro/RisqueAgregePage'))
const DashProConformiteUnifieePage = lazy(() => import('./pages/dashboard-pro/ConformiteUnifieePage'))
const DashProTendancesPage = lazy(() => import('./pages/dashboard-pro/TendancesPage'))
const DashProHeatmapPage = lazy(() => import('./pages/dashboard-pro/HeatmapPage'))
const DashProCorrelationsPage = lazy(() => import('./pages/dashboard-pro/CorrelationsPage'))
const DashProComparaisonPage = lazy(() => import('./pages/dashboard-pro/ComparaisonPage'))
const DashProBenchmarksPage = lazy(() => import('./pages/dashboard-pro/BenchmarksPage'))
const DashProDrillDownPage = lazy(() => import('./pages/dashboard-pro/DrillDownPage'))
const DashProCSuitePage = lazy(() => import('./pages/dashboard-pro/CSuitePage'))
const DashProTableauCAPage = lazy(() => import('./pages/dashboard-pro/TableauCAPage'))
const DashProRapportCAPage = lazy(() => import('./pages/dashboard-pro/RapportCAPage'))
const DashProAlertesPage = lazy(() => import('./pages/dashboard-pro/AlertesPage'))
const DashProMultiSitePage = lazy(() => import('./pages/dashboard-pro/MultiSitePage'))
const DashProDeploiementPage = lazy(() => import('./pages/dashboard-pro/DeploiementPage'))
const DashProPredictiveAnalyticsPage = lazy(() => import('./pages/dashboard-pro/PredictiveAnalyticsPage'))
const DashProCorrelationEnginePage = lazy(() => import('./pages/dashboard-pro/CorrelationEnginePage'))
const DashProNLQueryPage = lazy(() => import('./pages/dashboard-pro/NLQueryPage'))
const DashProAutoBenchmarkPage = lazy(() => import('./pages/dashboard-pro/AutoBenchmarkPage'))
const DashProOutilsPage = lazy(() => import('./pages/dashboard-pro/OutilsPage'))
const DashProAuditTrailPage = lazy(() => import('./pages/dashboard-pro/AuditTrailPage'))
const DashProIntegrationPage = lazy(() => import('./pages/dashboard-pro/IntegrationPage'))
const DashProConfigPage = lazy(() => import('./pages/dashboard-pro/ConfigPage'))
const DashProSettingsPage = lazy(() => import('./pages/dashboard-pro/SettingsPage'))
const PsaCockpitPage = lazy(() => import('./pages/dashboard-pro/PsaCockpitPage'))
const PsaTimesheetPage = lazy(() => import('./pages/dashboard-pro/PsaTimesheetPage'))

// Ethics Pro
const EthProHubPage = lazy(() => import('./pages/ethics-pro/HubPage'))
const EthProCockpitPage = lazy(() => import('./pages/ethics-pro/CockpitPage'))
const EthProScorePage = lazy(() => import('./pages/ethics-pro/ScorePage'))
const EthProCodeConduitePage = lazy(() => import('./pages/ethics-pro/CodeConduitePage'))
const EthProPolitiquesPage = lazy(() => import('./pages/ethics-pro/PolitiquesPage'))
const EthProComiteEthiquePage = lazy(() => import('./pages/ethics-pro/ComiteEthiquePage'))
const EthProSignalementsPage = lazy(() => import('./pages/ethics-pro/SignalementsPage'))
const EthProEnquetesPage = lazy(() => import('./pages/ethics-pro/EnquetesPage'))
const EthProConflitInteretPage = lazy(() => import('./pages/ethics-pro/ConflitInteretPage'))
const EthProAntiCorruptionPage = lazy(() => import('./pages/ethics-pro/AntiCorruptionPage'))
const EthProSapinIIPage = lazy(() => import('./pages/ethics-pro/SapinIIPage'))
const EthProIso37001Page = lazy(() => import('./pages/ethics-pro/Iso37001Page'))
const EthProDiversitePage = lazy(() => import('./pages/ethics-pro/DiversitePage'))
const EthProFormationPage = lazy(() => import('./pages/ethics-pro/FormationPage'))
const EthProCulturePage = lazy(() => import('./pages/ethics-pro/CulturePage'))
const EthProMaturitePage = lazy(() => import('./pages/ethics-pro/MaturitePage'))
const EthProEsgPage = lazy(() => import('./pages/ethics-pro/EsgPage'))
const EthProAlertePage = lazy(() => import('./pages/ethics-pro/AlertePage'))
const EthProCertificationPage = lazy(() => import('./pages/ethics-pro/CertificationPage'))
const EthProFrameworksPage = lazy(() => import('./pages/ethics-pro/FrameworksPage'))
const EthProOutilsPage = lazy(() => import('./pages/ethics-pro/OutilsPage'))
const EthProReportExecPage = lazy(() => import('./pages/ethics-pro/ReportExecPage'))
const EthProAuditTrailPage = lazy(() => import('./pages/ethics-pro/AuditTrailPage'))
const EthProIntegrationPage = lazy(() => import('./pages/ethics-pro/IntegrationPage'))
const EthProSettingsPage = lazy(() => import('./pages/ethics-pro/SettingsPage'))

// Strategy Pro
const StratProHubPage = lazy(() => import('./pages/strategy-pro/HubPage'))
const StratProCockpitPage = lazy(() => import('./pages/strategy-pro/CockpitPage'))
const StratProScorePage = lazy(() => import('./pages/strategy-pro/ScorePage'))
const StratProVisionPage = lazy(() => import('./pages/strategy-pro/VisionPage'))
const StratProObjectifsPage = lazy(() => import('./pages/strategy-pro/ObjectifsPage'))
const StratProKeyResultsPage = lazy(() => import('./pages/strategy-pro/KeyResultsPage'))
const StratProOkrDashboardPage = lazy(() => import('./pages/strategy-pro/OkrDashboardPage'))
const StratProInitiativesPage = lazy(() => import('./pages/strategy-pro/InitiativesPage'))
const StratProProgramsPage = lazy(() => import('./pages/strategy-pro/ProgramsPage'))
const StratProPortfolioPage = lazy(() => import('./pages/strategy-pro/PortfolioPage'))
const StratProAlignementPage = lazy(() => import('./pages/strategy-pro/AlignementPage'))
const StratProBscDashboardPage = lazy(() => import('./pages/strategy-pro/BscDashboardPage'))
const StratProPerspectivesPage = lazy(() => import('./pages/strategy-pro/PerspectivesPage'))
const StratProStrategicMapPage = lazy(() => import('./pages/strategy-pro/StrategicMapPage'))
const StratProSwotPage = lazy(() => import('./pages/strategy-pro/SwotPage'))
const StratProPestelPage = lazy(() => import('./pages/strategy-pro/PestelPage'))
const StratProBusinessCasePage = lazy(() => import('./pages/strategy-pro/BusinessCasePage'))
const StratProRoiPage = lazy(() => import('./pages/strategy-pro/RoiPage'))
const StratProGovernancePage = lazy(() => import('./pages/strategy-pro/GovernancePage'))
const StratProFrameworksPage = lazy(() => import('./pages/strategy-pro/FrameworksPage'))
const StratProPipelineIntelligencePage = lazy(() => import('./pages/strategy-pro/PipelineIntelligencePage'))
const StratProRevenueForecastPage = lazy(() => import('./pages/strategy-pro/RevenueForecastPage'))
const StratProClientHealthScorePage = lazy(() => import('./pages/strategy-pro/ClientHealthScorePage'))
const StratProCrossSellRecommenderPage = lazy(() => import('./pages/strategy-pro/CrossSellRecommenderPage'))
const StratProBoardReportGeneratorPage = lazy(() => import('./pages/strategy-pro/BoardReportGeneratorPage'))
const StratProCabinetBenchmarkPage = lazy(() => import('./pages/strategy-pro/CabinetBenchmarkPage'))
const StratProMissionPlannerPage = lazy(() => import('./pages/strategy-pro/MissionPlannerPage'))
const StratProQualityGatePage = lazy(() => import('./pages/strategy-pro/QualityGatePage'))
const StratProDeliverableDraftingPage = lazy(() => import('./pages/strategy-pro/DeliverableDraftingPage'))
const StratProClientCommHubPage = lazy(() => import('./pages/strategy-pro/ClientCommHubPage'))
const StratProTimeBudgetTrackerPage = lazy(() => import('./pages/strategy-pro/TimeBudgetTrackerPage'))
const StratProLessonsLearnedPage = lazy(() => import('./pages/strategy-pro/LessonsLearnedPage'))
const StratProOutilsPage = lazy(() => import('./pages/strategy-pro/OutilsPage'))
const StratProReportExecPage = lazy(() => import('./pages/strategy-pro/ReportExecPage'))
const StratProAuditTrailPage = lazy(() => import('./pages/strategy-pro/AuditTrailPage'))
const StratProIntegrationPage = lazy(() => import('./pages/strategy-pro/IntegrationPage'))
const StratProSettingsPage = lazy(() => import('./pages/strategy-pro/SettingsPage'))
// PSA #20 — Cabinet Professional Services Automation cockpit (/strategy-pro/psa)
const StratProPsaCockpitPage = lazy(() => import('./pages/strategy-pro/PsaCockpitPage'))

// Workspace Pro
const WkspProHubPage = lazy(() => import('./pages/workspace-pro/HubPage'))
const WkspProChatPage = lazy(() => import('./pages/workspace-pro/ChatPage'))
const WkspProDocumentsPage = lazy(() => import('./pages/workspace-pro/DocumentsPage'))
const WkspProMembersPage = lazy(() => import('./pages/workspace-pro/MembersPage'))
const WkspProPermissionsPage = lazy(() => import('./pages/workspace-pro/PermissionsPage'))
const WkspProInvitationsPage = lazy(() => import('./pages/workspace-pro/InvitationsPage'))
const WkspProAgentsPage = lazy(() => import('./pages/workspace-pro/AgentsPage'))
const WkspProRagIndexPage = lazy(() => import('./pages/workspace-pro/RagIndexPage'))
const WkspProSearchPage = lazy(() => import('./pages/workspace-pro/SearchPage'))
const WkspProNotificationsPage = lazy(() => import('./pages/workspace-pro/NotificationsPage'))
const WkspProActivityPage = lazy(() => import('./pages/workspace-pro/ActivityPage'))
const WkspProReportsPage = lazy(() => import('./pages/workspace-pro/ReportsPage'))
const WkspProChatHistoryPage = lazy(() => import('./pages/workspace-pro/ChatHistoryPage'))
const WkspProProfilePage = lazy(() => import('./pages/workspace-pro/ProfilePage'))
const WkspProThemesPage = lazy(() => import('./pages/workspace-pro/ThemesPage'))
const WkspProQuickStartPage = lazy(() => import('./pages/workspace-pro/QuickStartPage'))
const WkspProWebAccessPage = lazy(() => import('./pages/workspace-pro/WebAccessPage'))
const WkspProCommunityPage = lazy(() => import('./pages/workspace-pro/CommunityPage'))
const WkspProWorkspaceListPage = lazy(() => import('./pages/workspace-pro/WorkspaceListPage'))
const WkspProToolsPage = lazy(() => import('./pages/workspace-pro/ToolsPage'))
const WkspProOutilsPage = lazy(() => import('./pages/workspace-pro/OutilsPage'))
const WkspProModuleSettingsPage = lazy(() => import('./pages/workspace-pro/ModuleSettingsPage'))
const WkspProAuditTrailPage = lazy(() => import('./pages/workspace-pro/AuditTrailPage'))
const WkspProIntegrationPage = lazy(() => import('./pages/workspace-pro/IntegrationPage'))
const WkspProSettingsPage = lazy(() => import('./pages/workspace-pro/SettingsPage'))

// Framework Engine
const FrameworkPortfolioPage = lazy(() => import('./pages/framework-engine/FrameworkPortfolioPage'))
const FrameworkCatchAll = lazy(() => import('./pages/framework-engine/FrameworkPortfolioPage'))

// Global Views (Vue Globale — no specific client)
const GlobalDossiersPage = lazy(() => import('./pages/global/GlobalDossiers'))
const GlobalDocumentsPage = lazy(() => import('./pages/global/GlobalDocuments'))
const GlobalSignaturesPage = lazy(() => import('./pages/global/GlobalSignatures'))
const GlobalCalendarPage = lazy(() => import('./pages/global/GlobalCalendar'))
const GlobalMessagesPage = lazy(() => import('./pages/global/GlobalMessages'))

// Billing
const BillingPage = lazy(() => import('./pages/billing/BillingPage'))

// Legal
const LegalHubPage = lazy(() => import('./pages/legal/LegalHubPage'))
const VeilleReglementairePage = lazy(() => import('./pages/legal/VeilleReglementairePage'))
const ObligationsContractuellesPage = lazy(() => import('./pages/legal/ObligationsContractuellesPage'))
const ProtectionDonneesPage = lazy(() => import('./pages/legal/ProtectionDonneesPage'))
const ContentieuxPage = lazy(() => import('./pages/legal/ContentieuxPage'))
const ProprieteIntellectuellePage = lazy(() => import('./pages/legal/ProprieteIntellectuellePage'))
const GouvernanceCorporativePage = lazy(() => import('./pages/legal/GouvernanceCorporativePage'))
const DroitTravailPage = lazy(() => import('./pages/legal/DroitTravailPage'))
const FiscalitePage = lazy(() => import('./pages/legal/FiscalitePage'))
const EnvironnementPage = lazy(() => import('./pages/legal/EnvironnementPage'))

// New: Prospection, Offers, Contracts, Relationships, Team
const ProspectionPage = lazy(() => import('./pages/prospection/ProspectionPage'))
// COLLAB-08: Marketing (campaigns + content library)
const MarketingPage = lazy(() => import('./pages/marketing/MarketingPage'))
const OffersPage = lazy(() => import('./pages/offers/OffersPage'))
const ContractsPage = lazy(() => import('./pages/contracts/ContractsPage'))
const RelationshipsPage = lazy(() => import('./pages/relationships/RelationshipsPage'))
const TeamPage = lazy(() => import('./pages/team/TeamPage'))

// Mandats
const CreateMandatPage = lazy(() => import('./pages/mandats/CreateMandatPage'))
const MandatDetailPage = lazy(() => import('./pages/mandats/MandatDetailPage'))

// Governance Pro — additional sub-pages
const GovernancePoliciesPage = lazy(() => import('./pages/governance-pro/PoliciesPage'))
const GovernanceProceduresPage = lazy(() => import('./pages/governance-pro/ProceduresPage'))
const GovernanceDirectivesPage = lazy(() => import('./pages/governance-pro/DirectivesPage'))
const GovernanceRolesPage = lazy(() => import('./pages/governance-pro/RolesPage'))
const GovernanceOperatingModelPage = lazy(() => import('./pages/governance-pro/OperatingModelPage'))
const GovernanceCompliancePage = lazy(() => import('./pages/governance-pro/CompliancePage'))
const GovernanceRiskRegisterPage = lazy(() => import('./pages/governance-pro/RiskRegisterPage'))
const GovernanceControlsAuditPage = lazy(() => import('./pages/governance-pro/ControlsAuditPage'))
const GovernanceAuthorityRegisterPage = lazy(() => import('./pages/governance-pro/AuthorityRegisterPage'))
const GovernanceAIImpactPage = lazy(() => import('./pages/governance-pro/AIImpactPage'))
const GovernanceStrategicAnalysisPage = lazy(() => import('./pages/governance-pro/StrategicAnalysisPage'))
const GovernanceMaturityBridgePage = lazy(() => import('./pages/governance-pro/MaturityBridgePage'))
const GovernanceBenchmarkPage = lazy(() => import('./pages/governance-pro/BenchmarkPage'))
const GovernanceAlignmentPage = lazy(() => import('./pages/governance-pro/AlignmentPage'))
const GovernanceDataProgramPage = lazy(() => import('./pages/governance-pro/DataProgramPage'))
const GovernanceAIProgramPage = lazy(() => import('./pages/governance-pro/AIProgramPage'))
const GovernanceCloudProgramPage = lazy(() => import('./pages/governance-pro/CloudProgramPage'))
const GovernanceCulturePage = lazy(() => import('./pages/governance-pro/CulturePage'))
const GovernanceTargetsPage = lazy(() => import('./pages/governance-pro/TargetsPage'))
const GovernanceProcessesPage = lazy(() => import('./pages/governance-pro/ProcessesPage'))
const GovernanceOpsGovernancePage = lazy(() => import('./pages/governance-pro/OpsGovernancePage'))
const GovernanceRiskProfilePage = lazy(() => import('./pages/governance-pro/RiskProfilePage'))
const GovernanceReportsPage = lazy(() => import('./pages/governance-pro/ReportsPage'))
const GovernanceTemplatesPage = lazy(() => import('./pages/governance-pro/TemplatesPage'))
const GovernanceIntegrationPage = lazy(() => import('./pages/governance-pro/IntegrationPage'))
const GovernanceAuditTrailPage = lazy(() => import('./pages/governance-pro/AuditTrailPage'))
const GovernanceSettingsPage = lazy(() => import('./pages/governance-pro/SettingsPage'))
const GovernanceOutilsPage = lazy(() => import('./pages/governance-pro/OutilsPage'))
const GovernanceFrameworkMapperPage = lazy(() => import('./pages/governance-pro/FrameworkMapperPage'))
const GovernanceControlTestWizardPage = lazy(() => import('./pages/governance-pro/ControlTestWizardPage'))
const GovernanceSmartQuestionnairePage = lazy(() => import('./pages/governance-pro/SmartQuestionnairePage'))
const GovernanceEvidenceAutoCollectorPage = lazy(() => import('./pages/governance-pro/EvidenceAutoCollectorPage'))
const GovernanceRegulatoryRadarPage = lazy(() => import('./pages/governance-pro/RegulatoryRadarPage'))

// Bottom nav pages
const RegulatoryPage = lazy(() => import('./pages/regulatory/RegulatoryPage'))
const AssistantPage = lazy(() => import('./pages/assistant/AssistantPage'))
// AI-07 — RPA / Automation Flows management surface (governance-service /api/v1/rpa/*)
const AutomationFlowsPage = lazy(() => import('./pages/automation/FlowsPage'))
// Subsystem Q — Visual Workflow Builder (emits rpa_flows the Flow Runner executes)
const WorkflowBuilderPage = lazy(() => import('./pages/workflow-builder/WorkflowBuilderPage'))
// Business Page Builder — drag-and-drop public storefront ("Ma Page", paid add-on)
const MaPageBuilderPage = lazy(() => import('./pages/ma-page/MaPageBuilderPage'))
const PublicBusinessPage = lazy(() => import('./pages/ma-page/PublicBusinessPage'))
const AdminPage = lazy(() => import('./pages/admin/AdminPage'))
const SupportPage = lazy(() => import('./pages/support/SupportPage'))
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'))

// Admin — Organizations
const OrganizationsPage = lazy(() => import('./pages/admin/organizations/OrganizationsPage'))
const OrganizationHierarchyPage = lazy(() => import('./pages/admin/organizations/OrganizationHierarchyPage'))
const OrganizationSettingsPage = lazy(() => import('./pages/admin/organizations/OrganizationSettingsPage'))

// Admin — API Keys, Webhooks, Audit, Integrations
const AdminApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'))
const AdminWebhooksPage = lazy(() => import('./pages/admin/WebhooksPage'))
const AdminAuditPage = lazy(() => import('./pages/admin/AuditPage'))
const AdminIntegrationsPage = lazy(() => import('./pages/admin/IntegrationsPage'))

// Admin — AIGOV360 M13 Continuous-Watch (VEILLE) + M13/legal-status config surfaces
const AigovWatchSourcesPage = lazy(() => import('./pages/admin/aigov/WatchSourcesPage'))
const AigovPackEntitlementsPage = lazy(() => import('./pages/admin/aigov/PackEntitlementsPage'))
const AigovRoutingRulesPage = lazy(() => import('./pages/admin/aigov/RoutingRulesPage'))
const AigovLegalStatusPage = lazy(() => import('./pages/admin/aigov/LegalStatusPage'))
const AigovPackChangeRequestsPage = lazy(() => import('./pages/admin/aigov/PackChangeRequestsPage'))
const AigovWatchKpisPage = lazy(() => import('./pages/admin/aigov/WatchKpisPage'))

// Team — Roles, Permissions, Invitations
const RolesPage = lazy(() => import('./pages/team/RolesPage'))
const PermissionsPage = lazy(() => import('./pages/team/PermissionsPage'))
const InvitationsPage = lazy(() => import('./pages/team/InvitationsPage'))

// Account — Subscription, Plans, Credits, Features, Quotas
const SubscriptionPage = lazy(() => import('./pages/account/SubscriptionPage'))
const PlansPage = lazy(() => import('./pages/account/PlansPage'))
const CancelSubscriptionPage = lazy(() => import('./pages/account/CancelSubscriptionPage'))
const CreditsPage = lazy(() => import('./pages/account/credits/CreditsPage'))
const BuyCreditsPage = lazy(() => import('./pages/account/credits/BuyCreditsPage'))
const CreditsHistoryPage = lazy(() => import('./pages/account/credits/CreditsHistoryPage'))
const CreditsTransferPage = lazy(() => import('./pages/account/credits/CreditsTransferPage'))
const CreditsAllocatePage = lazy(() => import('./pages/account/credits/CreditsAllocatePage'))
const CreditsUsagePage = lazy(() => import('./pages/account/credits/CreditsUsagePage'))
const FeaturesPage = lazy(() => import('./pages/account/FeaturesPage'))
const QuotasPage = lazy(() => import('./pages/account/QuotasPage'))

// Billing — Invoice Detail, Payment Methods, Tax Receipts
const InvoiceDetailPage = lazy(() => import('./pages/billing/InvoiceDetailPage'))
const PaymentMethodsPage = lazy(() => import('./pages/billing/PaymentMethodsPage'))
const TaxReceiptsPage = lazy(() => import('./pages/billing/TaxReceiptsPage'))

// Settings — Profile, Notifications, Security, Developer, Privacy
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage'))
const AppearancePage = lazy(() => import('./pages/settings/AppearancePage'))
const SettingsNotificationsPage = lazy(() => import('./pages/settings/NotificationsPage'))
const SecurityPage = lazy(() => import('./pages/settings/SecurityPage'))
const DeveloperPage = lazy(() => import('./pages/settings/DeveloperPage'))
const PrivacyPage = lazy(() => import('./pages/settings/PrivacyPage'))
// MULTI-JURISDICTION P6 — cabinet multi-zone / primary-jurisdiction picker
const JurisdictionsPage = lazy(() => import('./pages/settings/JurisdictionsPage'))
// Custom Fields — tenant admin defines dynamic fields per entity-type
const CustomFieldsPage = lazy(() => import('./pages/settings/CustomFieldsPage'))
// Cabinet (comptable/fiscaliste) settings — config-driven generic section page
const SettingsSectionPage = lazy(() => import('./pages/settings/SettingsSectionPage'))

// Wave 4 Shard 29 — Generic stub for under-construction routes
const StubPage = lazy(() => import('./pages/StubPage'))

// Wave 5 — 31 W4-S29 stubs replaced with real, minimal-but-functional CRUD pages
// (see src/components/shared/CrudPage.tsx for the shared scaffold)
const CostPerQueryPage = lazy(() => import('./pages/ai-ia-pro/CostPerQueryPage'))
const DataopsPipelineMonitorPage = lazy(() => import('./pages/ai-ia-pro/DataopsPipelineMonitorPage'))
const DataProductCatalogPage = lazy(() => import('./pages/ai-ia-pro/DataProductCatalogPage'))
const SchemaEvolutionTrackerPage = lazy(() => import('./pages/ai-ia-pro/SchemaEvolutionTrackerPage'))
const CertificationPathwayPage = lazy(() => import('./pages/change-pro/CertificationPathwayPage'))
const CompetencyMatrixPage = lazy(() => import('./pages/change-pro/CompetencyMatrixPage'))
const LearningAnalyticsPage = lazy(() => import('./pages/change-pro/LearningAnalyticsPage'))
const LmsIntegrationPage = lazy(() => import('./pages/change-pro/LmsIntegrationPage'))
const BayesianNetworkPage = lazy(() => import('./pages/dashboard-pro/BayesianNetworkPage'))
const CohortAnalysisPage = lazy(() => import('./pages/dashboard-pro/CohortAnalysisPage'))
const MonteCarloPage = lazy(() => import('./pages/dashboard-pro/MonteCarloPage'))
const WhatIfBuilderPage = lazy(() => import('./pages/dashboard-pro/WhatIfBuilderPage'))
const CrossReferenceCheckerPage = lazy(() => import('./pages/governance-pro/CrossReferenceCheckerPage'))
const InterviewTranscriptAnalyzerPage = lazy(() => import('./pages/governance-pro/InterviewTranscriptAnalyzerPage'))
const RegulatoryDatabaseSearchPage = lazy(() => import('./pages/governance-pro/RegulatoryDatabaseSearchPage'))
const TemplateIntelligencePage = lazy(() => import('./pages/governance-pro/TemplateIntelligencePage'))
const WorkingPaperReviewerPage = lazy(() => import('./pages/governance-pro/WorkingPaperReviewerPage'))
const IncidentForensicsPage = lazy(() => import('./pages/security-pro/IncidentForensicsPage'))
const RedTeamPlannerPage = lazy(() => import('./pages/security-pro/RedTeamPlannerPage'))
const SecurityArchitectureReviewPage = lazy(() => import('./pages/security-pro/SecurityArchitectureReviewPage'))
const SupplyChainCyberRiskPage = lazy(() => import('./pages/security-pro/SupplyChainCyberRiskPage'))
const ClientExpectationTrackerPage = lazy(() => import('./pages/strategy-pro/ClientExpectationTrackerPage'))
const ClientNpsCsatPage = lazy(() => import('./pages/strategy-pro/ClientNpsCsatPage'))
const CompetitiveWinLossPage = lazy(() => import('./pages/strategy-pro/CompetitiveWinLossPage'))
const DealRoomPage = lazy(() => import('./pages/strategy-pro/DealRoomPage'))
const DeliverableQualityScorerPage = lazy(() => import('./pages/strategy-pro/DeliverableQualityScorerPage'))
const PartnerEcosystemPage = lazy(() => import('./pages/strategy-pro/PartnerEcosystemPage'))
const ResourceOptimizerPage = lazy(() => import('./pages/strategy-pro/ResourceOptimizerPage'))
const RevenueAttributionPage = lazy(() => import('./pages/strategy-pro/RevenueAttributionPage'))
const ScopeCreepDetectorPage = lazy(() => import('./pages/strategy-pro/ScopeCreepDetectorPage'))
const SprintRetrospectiveAiPage = lazy(() => import('./pages/strategy-pro/SprintRetrospectiveAiPage'))

// Wave 4 Shard 29 — Orphan page files now wired
// data-pro orphans
const DataAuditTrailPage = lazy(() => import('./pages/data-pro/AuditTrailPage'))
const DataIntegrationOrphanPage = lazy(() => import('./pages/data-pro/IntegrationPage'))
const DataMaturityModelsPage = lazy(() => import('./pages/data-pro/MaturityModelsPage'))
const DataOutilsPage = lazy(() => import('./pages/data-pro/OutilsPage'))
// framework-engine orphans
const FrameworkCatalogPage = lazy(() => import('./pages/framework-engine/FrameworkCatalogPage'))
const FrameworkRespondPage = lazy(() => import('./pages/framework-engine/FrameworkRespondPage'))
const FrameworkAdoptionWizard = lazy(() => import('./pages/framework-engine/FrameworkAdoptionWizard'))
const FrameworkUpdateManager = lazy(() => import('./pages/framework-engine/FrameworkUpdateManager'))
// risk-pro orphans
const RiskAppetitePage = lazy(() => import('./pages/risk-pro/AppetitePage'))
const RiskAssessmentPage = lazy(() => import('./pages/risk-pro/AssessmentPage'))
const RiskAuditTrailPage = lazy(() => import('./pages/risk-pro/AuditTrailPage'))
const RiskCockpitPage = lazy(() => import('./pages/risk-pro/CockpitPage'))
const RiskIntegrationPage = lazy(() => import('./pages/risk-pro/IntegrationPage'))
const RiskMatrixPage = lazy(() => import('./pages/risk-pro/MatrixPage'))
const RiskMonitoringPage = lazy(() => import('./pages/risk-pro/MonitoringPage'))
const RiskOutilsPage = lazy(() => import('./pages/risk-pro/OutilsPage'))
const RiskQuantitativePage = lazy(() => import('./pages/risk-pro/QuantitativePage'))
// Gap #24 — Monte-Carlo simulation (VaR/CVaR, OpRisk LDA capital, what-if)
const RiskMonteCarloPage = lazy(() => import('./pages/risk-pro/MonteCarloPage'))
const RiskReportRiskPage = lazy(() => import('./pages/risk-pro/ReportRiskPage'))
const RiskThreeLinesPage = lazy(() => import('./pages/risk-pro/ThreeLinesPage'))
// security-pro orphans
const SecurityEncryptionPage = lazy(() => import('./pages/security-pro/EncryptionPage'))
const SecurityIamPage = lazy(() => import('./pages/security-pro/IamPage'))
const SecurityOutilsPage = lazy(() => import('./pages/security-pro/OutilsPage'))
const SecuritySocDashboardPage = lazy(() => import('./pages/security-pro/SocDashboardPage'))

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes (no MainLayout, no Keycloak login-required) */}
        <Route path="/signup" element={<SignupPage />} />
        {/* Public storefront — published "Ma Page" (unauthenticated, slug-resolved) */}
        <Route path="/p/:slug" element={<PublicBusinessPage />} />
        {/* Public build/version page — no login required */}
        <Route path="/versionsbuilt" element={<VersionsBuiltPage />} />

        <Route element={<MainLayout />}>
          {/* Default redirect */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Core */}
          <Route path="/dashboard" element={<CabinetDashboardPage />} />
          <Route path="/hub" element={<HubPage />} />
          <Route path="/clients" element={<ClientsListPage />} />
          {/* Unify create on the list-page modal (was standalone ClientCreatePage). */}
          <Route path="/clients/new" element={<Navigate to="/clients?create=1" replace />} />
          <Route path="/mandats" element={<MandatsListPage />} />
          <Route path="/ads" element={<AdsManagerPage />} />
          {/* Unify create on the list-page modal (was standalone CreateMandatPage). */}
          <Route path="/mandats/new" element={<Navigate to="/mandats?create=1" replace />} />
          <Route path="/mandats/create" element={<Navigate to="/mandats?create=1" replace />} />
          {/* COLLAB-03: Planification des engagements (plans audit/evaluation + jalons) */}
          <Route path="/planning" element={<PlanningPage />} />
          <Route path="/mandats/planning" element={<PlanningPage />} />
          {/* COLLAB-09: Avancement des engagements + rapports de statut */}
          <Route path="/engagements/progress" element={<EngagementProgressPage />} />
          <Route path="/mandats/progress" element={<EngagementProgressPage />} />
          {/* COLLAB-11: Hub des engagements (vue consolidee + chronologie) */}
          <Route path="/engagements" element={<EngagementHubPage />} />
          <Route path="/engagements/hub" element={<EngagementHubPage />} />
          <Route path="/mandats/:mandatId" element={<MandatDetailPage />} />
          {/* COLLAB-01: Messagerie (provider<->client per-engagement threads) */}
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:threadId" element={<MessagesPage />} />
          {/* SOCIAL-LAYER (Feed): global social network feed (cabinet authors as 'business') */}
          <Route path="/feed" element={<SocialFeedPage authorKind="business" />} />
          {/* SOCIAL-LAYER (Forum): business-owned discussion forums */}
          <Route path="/forum" element={<ForumListPage />} />
          <Route path="/forum/:forumId" element={<ForumBoardPage />} />
          <Route path="/forum/:forumId/admin" element={<ForumAdminPage />} />
          <Route path="/forum/:forumId/topics/:topicId" element={<ForumTopicPage />} />
          {/* SOCIAL-LAYER (Booking): availability config + received-bookings inbox */}
          <Route path="/rendez-vous" element={<BookingSettingsPage />} />
          <Route path="/booking" element={<BookingSettingsPage />} />

          {/* Governance Pro */}
          <Route path="/governance-pro" element={<GovernanceHubPage />} />
          <Route path="/governance-pro/cockpit" element={<GovernanceCockpitPage />} />
          <Route path="/governance-pro/wizard" element={<GovernanceWizardPage />} />
          <Route path="/governance-pro/framework" element={<GovernanceFrameworkPage />} />
          <Route path="/governance-pro/standards" element={<GovernanceStandardsPage />} />
          <Route path="/governance-pro/model" element={<GovernanceModelPage />} />
          <Route path="/governance-pro/encadrement" element={<GovernanceEncadrementPage />} />
          <Route path="/governance-pro/grc" element={<GovernanceGRCDashboardPage />} />
          <Route path="/governance-pro/roadmap" element={<GovernanceRoadmapPage />} />
          <Route path="/governance-pro/policies" element={<GovernancePoliciesPage />} />
          <Route path="/governance-pro/procedures" element={<GovernanceProceduresPage />} />
          <Route path="/governance-pro/directives" element={<GovernanceDirectivesPage />} />
          <Route path="/governance-pro/roles" element={<GovernanceRolesPage />} />
          <Route path="/governance-pro/operating-model" element={<GovernanceOperatingModelPage />} />
          <Route path="/governance-pro/compliance" element={<GovernanceCompliancePage />} />
          <Route path="/governance-pro/risk-register" element={<GovernanceRiskRegisterPage />} />
          <Route path="/governance-pro/controls-audit" element={<GovernanceControlsAuditPage />} />
          <Route path="/governance-pro/authority-register" element={<GovernanceAuthorityRegisterPage />} />
          <Route path="/governance-pro/ai-impact" element={<GovernanceAIImpactPage />} />
          <Route path="/governance-pro/strategic-analysis" element={<GovernanceStrategicAnalysisPage />} />
          <Route path="/governance-pro/maturity-bridge" element={<GovernanceMaturityBridgePage />} />
          <Route path="/governance-pro/benchmark" element={<GovernanceBenchmarkPage />} />
          <Route path="/governance-pro/alignment" element={<GovernanceAlignmentPage />} />
          <Route path="/governance-pro/data-program" element={<GovernanceDataProgramPage />} />
          <Route path="/governance-pro/ai-program" element={<GovernanceAIProgramPage />} />
          <Route path="/governance-pro/cloud-program" element={<GovernanceCloudProgramPage />} />
          <Route path="/governance-pro/culture" element={<GovernanceCulturePage />} />
          <Route path="/governance-pro/targets" element={<GovernanceTargetsPage />} />
          <Route path="/governance-pro/processes" element={<GovernanceProcessesPage />} />
          <Route path="/governance-pro/ops-governance" element={<GovernanceOpsGovernancePage />} />
          <Route path="/governance-pro/risk-profile" element={<GovernanceRiskProfilePage />} />
          <Route path="/governance-pro/reports" element={<GovernanceReportsPage />} />
          <Route path="/governance-pro/templates" element={<GovernanceTemplatesPage />} />
          <Route path="/governance-pro/integration" element={<GovernanceIntegrationPage />} />
          <Route path="/governance-pro/audit-trail" element={<GovernanceAuditTrailPage />} />
          <Route path="/governance-pro/outils" element={<GovernanceOutilsPage />} />
          <Route path="/governance-pro/settings" element={<GovernanceSettingsPage />} />
          {/* Aliases for profile-MD sub-routes — map to nearest functional page */}
          <Route path="/governance-pro/diagnostic" element={<GovernanceCockpitPage />} />
          <Route path="/governance-pro/comites/new" element={<GovernanceRolesPage />} />
          <Route path="/governance-pro/interviews/log" element={<GovernanceWizardPage />} />
          <Route path="/governance-pro/policies/new" element={<GovernancePoliciesPage />} />
          <Route path="/governance-pro/raci/new" element={<GovernanceOperatingModelPage />} />
          <Route path="/governance-pro/target-model" element={<GovernanceTargetsPage />} />
          <Route path="/governance-pro/framework-mapper" element={<GovernanceFrameworkMapperPage />} />
          <Route path="/governance-pro/control-test-wizard" element={<GovernanceControlTestWizardPage />} />
          <Route path="/governance-pro/smart-questionnaire" element={<GovernanceSmartQuestionnairePage />} />
          <Route path="/governance-pro/evidence-auto-collector" element={<GovernanceEvidenceAutoCollectorPage />} />
          <Route path="/governance-pro/regulatory-radar" element={<GovernanceRegulatoryRadarPage />} />
          <Route path="/governance-pro/*" element={<Navigate to="/governance-pro" replace />} />

          {/* Security Pro */}
          <Route path="/security-pro" element={<SecurityHubPage />} />
          <Route path="/security-pro/cockpit" element={<SecurityCockpitPage />} />
          <Route path="/security-pro/score" element={<SecurityScorePage />} />
          <Route path="/security-pro/posture" element={<SecurityPosturePage />} />
          <Route path="/security-pro/alerts" element={<SecurityAlertsPage />} />
          <Route path="/security-pro/incidents" element={<SecurityIncidentsPage />} />
          <Route path="/security-pro/threat-intel" element={<SecurityThreatIntelPage />} />
          <Route path="/security-pro/vulns" element={<SecurityVulnsPage />} />
          <Route path="/security-pro/controls" element={<SecurityControlsPage />} />
          <Route path="/security-pro/bcp" element={<SecurityBcpPage />} />
          <Route path="/security-pro/dr" element={<SecurityDrPage />} />
          <Route path="/security-pro/iso27001" element={<SecurityIso27001Page />} />
          <Route path="/security-pro/soc2" element={<SecuritySoc2Page />} />
          <Route path="/security-pro/frameworks" element={<SecurityFrameworksPage />} />
          <Route path="/security-pro/templates" element={<SecurityTemplatesPage />} />
          <Route path="/security-pro/exercises" element={<SecurityExercisesPage />} />
          <Route path="/security-pro/report-exec" element={<SecurityReportExecPage />} />
          <Route path="/security-pro/report-ops" element={<SecurityReportOpsPage />} />
          <Route path="/security-pro/audit-trail" element={<SecurityAuditTrailPage />} />
          <Route path="/security-pro/integration" element={<SecurityIntegrationPage />} />
          <Route path="/security-pro/settings" element={<SecuritySettingsPage />} />
          <Route path="/security-pro/threat-intel-feed" element={<SecurityThreatIntelFeedPage />} />
          <Route path="/security-pro/pentest-parser" element={<SecurityPentestParserPage />} />
          <Route path="/security-pro/bcp-dr-simulator" element={<SecurityBcpDrSimulatorPage />} />
          <Route path="/security-pro/soc-maturity" element={<SecuritySocMaturityPage />} />
          <Route path="/security-pro/compliance-linker" element={<SecurityComplianceControlLinkerPage />} />
          {/* W4-S29 — orphan security-pro pages now wired */}
          <Route path="/security-pro/encryption" element={<SecurityEncryptionPage />} />
          <Route path="/security-pro/iam" element={<SecurityIamPage />} />
          <Route path="/security-pro/outils" element={<SecurityOutilsPage />} />
          <Route path="/security-pro/soc-dashboard" element={<SecuritySocDashboardPage />} />

          {/* Risk Pro */}
          <Route path="/risk-pro" element={<RiskHubPage />} />
          <Route path="/risk-pro/register" element={<RiskRegisterPage />} />
          <Route path="/risk-pro/identify" element={<RiskIdentifyWizardPage />} />
          <Route path="/risk-pro/scenarios" element={<RiskScenariosPage />} />
          <Route path="/risk-pro/bowtie" element={<RiskBowtiePage />} />
          <Route path="/risk-pro/controls" element={<RiskControlsPage />} />
          <Route path="/risk-pro/treatment" element={<RiskTreatmentPage />} />
          <Route path="/risk-pro/kri" element={<RiskKriPage />} />
          <Route path="/risk-pro/incidents" element={<RiskIncidentsPage />} />
          <Route path="/risk-pro/taxonomy" element={<RiskTaxonomyPage />} />
          <Route path="/risk-pro/templates" element={<RiskTemplatesPage />} />
          <Route path="/risk-pro/frameworks" element={<RiskFrameworksPage />} />
          <Route path="/risk-pro/report-board" element={<RiskReportBoardPage />} />
          <Route path="/risk-pro/settings" element={<RiskSettingsPage />} />
          {/* W4-S29 — orphan risk-pro pages now wired */}
          <Route path="/risk-pro/appetite" element={<RiskAppetitePage />} />
          <Route path="/risk-pro/assessment" element={<RiskAssessmentPage />} />
          <Route path="/risk-pro/audit-trail" element={<RiskAuditTrailPage />} />
          <Route path="/risk-pro/cockpit" element={<RiskCockpitPage />} />
          <Route path="/risk-pro/integration" element={<RiskIntegrationPage />} />
          <Route path="/risk-pro/matrix" element={<RiskMatrixPage />} />
          <Route path="/risk-pro/quantitative" element={<RiskQuantitativePage />} />
          <Route path="/risk-pro/report-risk" element={<RiskReportRiskPage />} />
          <Route path="/risk-pro/three-lines" element={<RiskThreeLinesPage />} />

          {/* Risk-Pro short aliases (test-plan compatibility) */}
          <Route path="/risk" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/hub" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/cockpit" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/register" element={<Navigate to="/risk-pro/register" replace />} />
          <Route path="/risk/matrix" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/bowtie" element={<Navigate to="/risk-pro/bowtie" replace />} />
          <Route path="/risk/score" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/appetite" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/assessment" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/identify-wizard" element={<Navigate to="/risk-pro/identify" replace />} />
          <Route path="/risk/scenarios" element={<Navigate to="/risk-pro/scenarios" replace />} />
          <Route path="/risk/controls" element={<Navigate to="/risk-pro/controls" replace />} />
          <Route path="/risk/treatment" element={<Navigate to="/risk-pro/treatment" replace />} />
          <Route path="/risk/kri" element={<Navigate to="/risk-pro/kri" replace />} />
          <Route path="/risk/monitoring" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/incidents" element={<Navigate to="/risk-pro/incidents" replace />} />
          <Route path="/risk/quantitative" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk/three-lines" element={<Navigate to="/risk-pro" replace />} />
          <Route path="/risk-pro/monitoring" element={<RiskMonitoringPage />} />
          {/* Gap #24 — matches contexts.ts nav href /risk-pro/montecarlo */}
          <Route path="/risk-pro/montecarlo" element={<RiskMonteCarloPage />} />
          <Route path="/risk-pro/outils" element={<RiskOutilsPage />} />

          {/* Data Pro */}
          <Route path="/data-pro" element={<DataHubPage />} />
          <Route path="/data-pro/cockpit" element={<DataCockpitPage />} />
          <Route path="/data-pro/score" element={<DataScorePage />} />
          <Route path="/data-pro/governance" element={<DataGovernancePage />} />
          <Route path="/data-pro/quality" element={<DataQualityPage />} />
          <Route path="/data-pro/mdm" element={<DataMDMPage />} />
          <Route path="/data-pro/architecture" element={<DataArchitecturePage />} />
          <Route path="/data-pro/metadata" element={<DataMetadataPage />} />
          <Route path="/data-pro/modeling" element={<DataModelingPage />} />
          <Route path="/data-pro/storage" element={<DataStoragePage />} />
          <Route path="/data-pro/security" element={<DataSecurityPage />} />
          <Route path="/data-pro/lifecycle" element={<DataLifecyclePage />} />
          <Route path="/data-pro/integration" element={<DataIntegrationPage />} />
          <Route path="/data-pro/dwh-bi" element={<DataDwhBiPage />} />
          <Route path="/data-pro/documents" element={<DataDocumentsPage />} />
          <Route path="/data-pro/dcam" element={<DataDcamPage />} />
          <Route path="/data-pro/cdmc" element={<DataCdmcPage />} />
          <Route path="/data-pro/maturite" element={<DataMaturitePage />} />
          <Route path="/data-pro/frameworks" element={<DataFrameworksPage />} />
          <Route path="/data-pro/report-exec" element={<DataReportExecPage />} />
          <Route path="/data-pro/settings" element={<DataSettingsPage />} />
          {/* W4-S29 — orphan data-pro pages now wired */}
          <Route path="/data-pro/audit-trail" element={<DataAuditTrailPage />} />
          <Route path="/data-pro/integration-tools" element={<DataIntegrationOrphanPage />} />
          <Route path="/data-pro/maturity-models" element={<DataMaturityModelsPage />} />
          <Route path="/data-pro/outils" element={<DataOutilsPage />} />
          {/* Data classification feature */}
          <Route path="/data-pro/classification" element={<ClassificationPage />} />
          <Route path="/data-pro/ai-readiness" element={<AIReadinessPage />} />

          {/* Audit Pro */}
          <Route path="/audit-pro" element={<AuditHubPage />} />
          <Route path="/audit-pro/cockpit" element={<AuditCockpitPage />} />
          <Route path="/audit-pro/calendar" element={<AuditCalendarPage />} />
          <Route path="/audit-pro/annual-plan" element={<AuditAnnualPlanPage />} />
          <Route path="/audit-pro/plan-wizard" element={<AuditPlanWizardPage />} />
          <Route path="/audit-pro/universe" element={<AuditUniversePage />} />
          <Route path="/audit-pro/missions" element={<AuditMissionsPage />} />
          <Route path="/audit-pro/findings" element={<AuditFindingsPage />} />
          <Route path="/audit-pro/actions" element={<AuditActionsPage />} />
          <Route path="/audit-pro/tracking" element={<AuditTrackingPage />} />
          <Route path="/audit-pro/self-assess" element={<AuditSelfAssessPage />} />
          <Route path="/audit-pro/reports" element={<AuditReportPage />} />
          <Route path="/audit-pro/cert-dashboard" element={<AuditCertDashboardPage />} />
          <Route path="/audit-pro/cert-gap" element={<AuditCertGapPage />} />
          <Route path="/audit-pro/cert-readiness" element={<AuditCertReadinessPage />} />
          <Route path="/audit-pro/cert-timeline" element={<AuditCertTimelinePage />} />
          <Route path="/audit-pro/audit-universe-ai" element={<AuditUniverseAIPage />} />
          <Route path="/audit-pro/workpaper-generator" element={<AuditWorkpaperGeneratorPage />} />
          <Route path="/audit-pro/finding-classifier" element={<AuditFindingClassifierPage />} />
          <Route path="/audit-pro/report-narrator" element={<AuditReportNarratorPage />} />
          <Route path="/audit-pro/follow-up-tracker-ai" element={<AuditFollowUpTrackerAIPage />} />
          <Route path="/audit-pro/settings" element={<AuditSettingsPage />} />
          {/* Wave1 fix: register pages whose files exist but had no <Route> */}
          <Route path="/audit-pro/engagement" element={<AuditEngagementPage />} />
          <Route path="/audit-pro/fieldwork" element={<AuditFieldworkPage />} />
          <Route path="/audit-pro/evidence" element={<AuditEvidencePage />} />
          <Route path="/audit-pro/interviews" element={<AuditInterviewsPage />} />
          <Route path="/audit-pro/workpapers" element={<AuditWorkpapersPage />} />
          <Route path="/audit-pro/recommendations" element={<AuditRecommendationsPage />} />
          <Route path="/audit-pro/quality-review" element={<AuditQualityReviewPage />} />
          <Route path="/audit-pro/methodology" element={<AuditMethodologyPage />} />
          <Route path="/audit-pro/templates" element={<AuditTemplatesPage />} />
          <Route path="/audit-pro/checklists" element={<AuditChecklistsPage />} />
          <Route path="/audit-pro/resources" element={<AuditResourcesPage />} />
          <Route path="/audit-pro/audit-trail" element={<AuditAuditTrailPage />} />
          <Route path="/audit-pro/committee" element={<AuditCommitteePage />} />
          <Route path="/audit-pro/cert-audit-workspace" element={<AuditCertAuditWorkspacePage />} />
          <Route path="/audit-pro/cert-body-portal" element={<AuditCertBodyPortalPage />} />
          <Route path="/audit-pro/cert-body-registry" element={<AuditCertBodyRegistryPage />} />
          <Route path="/audit-pro/cert-controls" element={<AuditCertControlsPage />} />
          <Route path="/audit-pro/cert-decision" element={<AuditCertDecisionPage />} />
          {/* AU-07: sidebar (AuditProLayout) links to plural /cert-decisions — alias to avoid SPA catch-all */}
          <Route path="/audit-pro/cert-decisions" element={<AuditCertDecisionPage />} />
          <Route path="/audit-pro/cert-doc-requirements" element={<AuditCertDocRequirementsPage />} />
          <Route path="/audit-pro/cert-evidence-requests" element={<AuditCertEvidenceRequestsPage />} />
          <Route path="/audit-pro/cert-projects" element={<AuditCertProjectsPage />} />
          <Route path="/audit-pro/cert-project-wizard" element={<AuditCertProjectWizardPage />} />
          <Route path="/audit-pro/cert-surveillance" element={<AuditCertSurveillancePage />} />
          <Route path="/audit-pro/client-portal" element={<AuditClientPortalPage />} />
          <Route path="/audit-pro/integration" element={<AuditIntegrationPage />} />
          <Route path="/audit-pro/outils" element={<AuditOutilsPage />} />
          {/* Wave3 S10 fix: 10 missing audit-pro routes wired with stub pages */}
          <Route path="/audit-pro/cert-nonconformities" element={<AuditCertNonconformitiesPage />} />
          <Route path="/audit-pro/continuous-audit-monitor" element={<AuditContinuousAuditMonitorPage />} />
          <Route path="/audit-pro/statistical-sampling" element={<AuditStatisticalSamplingPage />} />
          <Route path="/audit-pro/fraud-pattern-detector" element={<AuditFraudPatternDetectorPage />} />
          <Route path="/audit-pro/evidence-blockchain" element={<AuditEvidenceBlockchainPage />} />
          <Route path="/audit-pro/cae-dashboard" element={<AuditCaeDashboardPage />} />
          <Route path="/audit-pro/cert-scheduling" element={<AuditCertSchedulingPage />} />
          <Route path="/audit-pro/cert-certificates" element={<AuditCertCertificatesPage />} />
          <Route path="/audit-pro/cert-scope-adjust" element={<AuditCertScopeAdjustPage />} />
          {/* Retire duplicate cert-project wizard impl — unify on the canonical one. */}
          <Route path="/audit-pro/cert-project-wizard-alt" element={<Navigate to="/audit-pro/cert-project-wizard" replace />} />

          {/* AI/IA Pro */}
          <Route path="/ai-ia-pro" element={<AiIaHubPage />} />
          <Route path="/ai-ia-pro/cockpit" element={<AiIaCockpitPage />} />
          <Route path="/ai-ia-pro/score" element={<AiIaScorePage />} />
          <Route path="/ai-ia-pro/registre" element={<AiIaRegistrePage />} />
          <Route path="/ai-ia-pro/bias" element={<AiIaBiasPage />} />
          <Route path="/ai-ia-pro/eu-ai-act" element={<AiIaEUAIActPage />} />
          <Route path="/ai-ia-pro/iso42001" element={<AiIaIso42001Page />} />
          <Route path="/ai-ia-pro/nist-ai" element={<AiIaNistAIPage />} />
          <Route path="/ai-ia-pro/impact" element={<AiIaImpactPage />} />
          <Route path="/ai-ia-pro/readiness" element={<AiIaReadinessPage />} />
          <Route path="/ai-ia-pro/house-assessment" element={<AiIaHouseAssessmentPage />} />
          <Route path="/ai-ia-pro/ml-registry" element={<AiIaMlRegistryPage />} />
          <Route path="/ai-ia-pro/risk-class" element={<AiIaRiskClassPage />} />
          <Route path="/ai-ia-pro/prompt-gov" element={<AiIaPromptGovPage />} />
          <Route path="/ai-ia-pro/document-intelligence" element={<AiIaDocumentIntelligencePage />} />
          <Route path="/ai-ia-pro/frameworks" element={<AiIaFrameworksPage />} />
          <Route path="/ai-ia-pro/audit-trail" element={<AiIaAuditTrailPage />} />
          <Route path="/ai-ia-pro/integration" element={<AiIaIntegrationPage />} />
          <Route path="/ai-ia-pro/report-exec" element={<AiIaReportExecPage />} />
          <Route path="/ai-ia-pro/settings" element={<AiIaSettingsPage />} />
          <Route path="/ai-ia-pro/data-lineage" element={<AiIaDataLineageDiscoveryPage />} />
          <Route path="/ai-ia-pro/model-risk-scorer" element={<AiIaAIModelRiskScorerPage />} />
          <Route path="/ai-ia-pro/shadow-ai-detector" element={<AiIaShadowAIPage />} />
          <Route path="/ai-ia-pro/dpia-generator" element={<AiIaDPIAAutoGeneratorPage />} />
          <Route path="/ai-ia-pro/data-quality" element={<AiIaDataQualityDashboardPage />} />
          <Route path="/ai-ia-pro/data-gov-ai" element={<AiIaDataGovAIPage />} />
          <Route path="/ai-ia-pro/explain" element={<AiIaExplainPage />} />
          <Route path="/ai-ia-pro/genai-policies" element={<AiIaGenAIPoliciesPage />} />
          <Route path="/ai-ia-pro/lifecycle" element={<AiIaLifecyclePage />} />
          <Route path="/ai-ia-pro/mlops" element={<AiIaMLOpsPage />} />
          <Route path="/ai-ia-pro/outils" element={<AiIaOutilsPage />} />
          <Route path="/ai-ia-pro/performance" element={<AiIaPerformancePage />} />
          <Route path="/ai-ia-pro/bias-assessment" element={<AIBiasAssessmentPage />} />
          <Route path="/ai-ia-pro/impact-assessment" element={<AIImpactAssessmentPage />} />
          {/* AG-05: Multi-agentic Console */}
          <Route path="/ai-ia-pro/agent-console" element={<AgentConsolePage />} />
          <Route path="/agents-pro" element={<AgentConsolePage />} />
          <Route path="/agents-pro/console" element={<AgentConsolePage />} />
          {/* /ai-pro/* alias for legacy / sidebar paths */}
          <Route path="/ai-pro" element={<Navigate to="/ai-ia-pro" replace />} />
          <Route path="/ai-pro/*" element={<Navigate to="/ai-ia-pro" replace />} />
          {/* /ws/* alias for workspace-pro */}
          <Route path="/ws" element={<Navigate to="/workspace-pro" replace />} />
          <Route path="/ws/*" element={<Navigate to="/workspace-pro" replace />} />
          {/* /data/* alias for data-pro (more granular routes already exist below) */}
          <Route path="/data" element={<Navigate to="/data-pro" replace />} />
          <Route path="/data/*" element={<Navigate to="/data-pro" replace />} />
          {/* /strat/* alias for strategy-pro */}
          <Route path="/strat" element={<Navigate to="/strategy-pro" replace />} />
          <Route path="/strat/*" element={<Navigate to="/strategy-pro" replace />} />
          {/* /mat/* alias for maturity-pro */}
          <Route path="/mat" element={<Navigate to="/maturity-pro" replace />} />
          <Route path="/mat/*" element={<Navigate to="/maturity-pro" replace />} />
          {/* /bm/* alias for business-model-pro */}
          <Route path="/bm" element={<Navigate to="/business-model-pro" replace />} />
          <Route path="/bm/*" element={<Navigate to="/business-model-pro" replace />} />

          {/* Change Pro */}
          <Route path="/change-pro" element={<ChangeHubPage />} />
          <Route path="/change-pro/cockpit" element={<ChangeCockpitPage />} />
          <Route path="/change-pro/adkar" element={<ChangeAdkarPage />} />
          <Route path="/change-pro/kotter" element={<ChangeKotterPage />} />
          <Route path="/change-pro/lewin-bridges" element={<ChangeLewinBridgesPage />} />
          <Route path="/change-pro/mckinsey-7s" element={<ChangeMcKinsey7sPage />} />
          <Route path="/change-pro/roadmap" element={<ChangeRoadmapPage />} />
          <Route path="/change-pro/champions" element={<ChangeChampionsPage />} />
          <Route path="/change-pro/adoption" element={<ChangeAdoptionPage />} />
          <Route path="/change-pro/resistance" element={<ChangeResistancePage />} />
          <Route path="/change-pro/communication" element={<ChangeCommunicationPage />} />
          <Route path="/change-pro/formation" element={<ChangeFormationPage />} />
          <Route path="/change-pro/impact" element={<ChangeImpactPage />} />
          <Route path="/change-pro/culture" element={<ChangeCulturePage />} />
          <Route path="/change-pro/portfolio" element={<ChangePortfolioPage />} />
          <Route path="/change-pro/enps" element={<ChangeEnpsPage />} />
          <Route path="/change-pro/roi" element={<ChangeRoiPage />} />
          <Route path="/change-pro/maturite" element={<ChangeMaturitePage />} />
          <Route path="/change-pro/audit-trail" element={<ChangeAuditTrailPage />} />
          <Route path="/change-pro/integration" element={<ChangeIntegrationPage />} />
          <Route path="/change-pro/report-exec" element={<ChangeReportExecPage />} />
          <Route path="/change-pro/settings" element={<ChangeSettingsPage />} />
          <Route path="/change-pro/training-content-gen" element={<ChangeTrainingContentGenPage />} />
          <Route path="/change-pro/adoption-predictor" element={<ChangeAdoptionPredictorPage />} />
          <Route path="/change-pro/micro-learning" element={<ChangeMicroLearningPage />} />
          {/* Wave 3 Shard 6c — newly registered change-pro routes */}
          <Route path="/change-pro/score" element={<ChangeScorePage />} />
          <Route path="/change-pro/readiness" element={<ChangeReadinessPage />} />
          <Route path="/change-pro/outils" element={<ChangeOutilsPage />} />
          <Route path="/change-pro/adkar-assessment" element={<ADKARAssessmentPage />} />

          {/* Business Model Pro */}
          <Route path="/business-model-pro" element={<BmHubPage />} />
          <Route path="/business-model-pro/cockpit" element={<BmCockpitPage />} />
          <Route path="/business-model-pro/business-model" element={<BmBusinessModelPage />} />
          {/* BE3 fix — canvas cards navigate to /canvas/:id; open the BMC editor on that canvas */}
          <Route path="/business-model-pro/canvas/:id" element={<BmBusinessModelPage />} />
          <Route path="/business-model-pro/lean-canvas" element={<BmLeanCanvasPage />} />
          <Route path="/business-model-pro/value-proposition" element={<BmValuePropositionPage />} />
          <Route path="/business-model-pro/sessions" element={<BmSessionsPage />} />
          <Route path="/business-model-pro/score" element={<BmScorePage />} />
          <Route path="/business-model-pro/canvas-library" element={<BmCanvasLibraryPage />} />
          <Route path="/business-model-pro/custom-canvas" element={<BmCustomCanvasPage />} />
          <Route path="/business-model-pro/guided-questions" element={<BmGuidedQuestionsPage />} />
          <Route path="/business-model-pro/strategic-alignment" element={<BmStrategicAlignmentPage />} />
          <Route path="/business-model-pro/impact-analysis" element={<BmImpactAnalysisPage />} />
          <Route path="/business-model-pro/benchmarking" element={<BmBenchmarkingPage />} />
          <Route path="/business-model-pro/contributions" element={<BmContributionsPage />} />
          <Route path="/business-model-pro/plenary" element={<BmPlenaryPage />} />
          <Route path="/business-model-pro/consolidation" element={<BmConsolidationPage />} />
          <Route path="/business-model-pro/certification" element={<BmCertificationPage />} />
          <Route path="/business-model-pro/maturite" element={<BmMaturitePage />} />
          <Route path="/business-model-pro/frameworks" element={<BmFrameworksPage />} />
          <Route path="/business-model-pro/ai-project" element={<BmAiProjectPage />} />
          <Route path="/business-model-pro/audit-trail" element={<BmAuditTrailPage />} />
          <Route path="/business-model-pro/integrations" element={<BmIntegrationsPage />} />
          <Route path="/business-model-pro/report-exec" element={<BmReportExecPage />} />
          <Route path="/business-model-pro/outils" element={<BmOutilsPage />} />
          <Route path="/business-model-pro/settings" element={<BmSettingsPage />} />

          {/* Maturity Pro */}
          <Route path="/maturity-pro" element={<MaturityProHubPage />} />
          <Route path="/maturity-pro/cockpit" element={<MaturityProCockpitPage />} />
          <Route path="/maturity-pro/questionnaires" element={<MaturityProQuestionnairesPage />} />
          <Route path="/maturity-pro/benchmarking" element={<MaturityProBenchmarkingPage />} />
          <Route path="/maturity-pro/gap-analyse" element={<MaturityProGapAnalysePage />} />
          <Route path="/maturity-pro/progression" element={<MaturityProProgressionPage />} />
          <Route path="/maturity-pro/objectifs" element={<MaturityProObjectifsPage />} />
          <Route path="/maturity-pro/score" element={<MaturityProScorePage />} />
          <Route path="/maturity-pro/report-exec" element={<MaturityProReportExecPage />} />
          <Route path="/maturity-pro/settings" element={<MaturityProSettingsPage />} />
          {/* Wave 3 Shard 6 — newly registered maturity-pro routes (15 missing pages) */}
          <Route path="/maturity-pro/cmmi" element={<MaturityProCmmiPage />} />
          <Route path="/maturity-pro/cobit" element={<MaturityProCobitPage />} />
          <Route path="/maturity-pro/iso33001" element={<MaturityProIso33001Page />} />
          <Route path="/maturity-pro/frameworks" element={<MaturityProFrameworksPage />} />
          <Route path="/maturity-pro/custom-model" element={<MaturityProCustomModelPage />} />
          <Route path="/maturity-pro/dimensions" element={<MaturityProDimensionsPage />} />
          <Route path="/maturity-pro/mapping" element={<MaturityProMappingPage />} />
          <Route path="/maturity-pro/maturite" element={<MaturityProMaturitePage />} />
          <Route path="/maturity-pro/roadmap" element={<MaturityProRoadmapPage />} />
          <Route path="/maturity-pro/campaigns" element={<MaturityProCampaignsPage />} />
          <Route path="/maturity-pro/benchmarks" element={<MaturityProBenchmarksPage />} />
          <Route path="/maturity-pro/audit-trail" element={<MaturityProAuditTrailPage />} />
          <Route path="/maturity-pro/certification" element={<MaturityProCertificationPage />} />
          <Route path="/maturity-pro/integration" element={<MaturityProIntegrationPage />} />
          <Route path="/maturity-pro/outils" element={<MaturityProOutilsPage />} />
          {/* DCMM maturity feature */}
          <Route path="/maturity-pro/dcmm" element={<DcmmAssessmentsPage />} />
          <Route path="/maturity-pro/dcmm/:id" element={<DcmmDetailPage />} />

          {/* Compliance Pro */}
          <Route path="/compliance-pro" element={<CompProHubPage />} />
          <Route path="/compliance-pro/cockpit" element={<CompProCockpitPage />} />
          <Route path="/compliance-pro/score" element={<CompProScorePage />} />
          <Route path="/compliance-pro/rgpd" element={<CompProRgpdPage />} />
          <Route path="/compliance-pro/loi25" element={<CompProLoi25Page />} />
          <Route path="/compliance-pro/ccpa" element={<CompProCcpaPage />} />
          <Route path="/compliance-pro/pci-dss" element={<CompProPciDssPage />} />
          <Route path="/compliance-pro/sox" element={<CompProSoxPage />} />
          <Route path="/compliance-pro/iso27701" element={<CompProIso27701Page />} />
          <Route path="/compliance-pro/nist-privacy" element={<CompProNistPrivacyPage />} />
          <Route path="/compliance-pro/register" element={<CompProRegisterPage />} />
          <Route path="/compliance-pro/actions" element={<CompProActionsPage />} />
          <Route path="/compliance-pro/gap-analysis" element={<CompProGapAnalysisPage />} />
          <Route path="/compliance-pro/posture" element={<CompProPosturePage />} />
          <Route path="/compliance-pro/consent" element={<CompProConsentPage />} />
          <Route path="/compliance-pro/dsr" element={<CompProDsrPage />} />
          <Route path="/compliance-pro/pia" element={<CompProPiaPage />} />
          <Route path="/compliance-pro/transfers" element={<CompProTransfersPage />} />
          <Route path="/compliance-pro/certification" element={<CompProCertificationPage />} />
          <Route path="/compliance-pro/frameworks" element={<CompProFrameworksPage />} />
          <Route path="/compliance-pro/outils" element={<CompProOutilsPage />} />
          {/* Copilot v3 — Analyse d'écart réglementaire + Questionnaire IA */}
          <Route path="/compliance-pro/framework-gap" element={<CompProFrameworkGapPage />} />
          <Route path="/compliance-pro/questionnaire-ia" element={<CompProQuestionnaireAIPage />} />
          <Route path="/compliance-pro/report-exec" element={<CompProReportExecPage />} />
          <Route path="/compliance-pro/audit-trail" element={<CompProAuditTrailPage />} />
          <Route path="/compliance-pro/integration" element={<CompProIntegrationPage />} />
          <Route path="/compliance-pro/settings" element={<CompProSettingsPage />} />
          {/* Aliases for profile-MD sub-routes */}
          <Route path="/compliance-pro/aida/new" element={<CompProPiaPage />} />
          <Route path="/compliance-pro/diagnostic" element={<CompProCockpitPage />} />
          <Route path="/compliance-pro/evidence/collector" element={<CompProRegisterPage />} />
          <Route path="/compliance-pro/frameworks/iso42001" element={<CompProFrameworksPage />} />
          <Route path="/compliance-pro/gap-mapping" element={<CompProGapAnalysisPage />} />
          <Route path="/compliance-pro/gap-predictor" element={<CompProGapAnalysisPage />} />
          {/* Compliance-Pro short aliases (test-plan compatibility) */}
          <Route path="/comp" element={<Navigate to="/compliance-pro" replace />} />
          <Route path="/comp/hub" element={<Navigate to="/compliance-pro" replace />} />
          <Route path="/comp/cockpit" element={<Navigate to="/compliance-pro/cockpit" replace />} />
          <Route path="/comp/frameworks" element={<Navigate to="/compliance-pro/frameworks" replace />} />
          <Route path="/comp/gap-analysis" element={<Navigate to="/compliance-pro/gap-analysis" replace />} />
          <Route path="/comp/score" element={<Navigate to="/compliance-pro/score" replace />} />
          <Route path="/comp/register" element={<Navigate to="/compliance-pro/register" replace />} />
          <Route path="/comp/consent" element={<Navigate to="/compliance-pro/consent" replace />} />
          <Route path="/comp/dsr" element={<Navigate to="/compliance-pro/dsr" replace />} />
          <Route path="/comp/pia" element={<Navigate to="/compliance-pro/pia" replace />} />
          <Route path="/comp/ccpa" element={<Navigate to="/compliance-pro/ccpa" replace />} />
          <Route path="/comp/rgpd" element={<Navigate to="/compliance-pro/rgpd" replace />} />
          <Route path="/comp/iso27701" element={<Navigate to="/compliance-pro/iso27701" replace />} />
          <Route path="/comp/loi25" element={<Navigate to="/compliance-pro/loi25" replace />} />
          <Route path="/comp/pci-dss" element={<Navigate to="/compliance-pro/pci-dss" replace />} />
          <Route path="/comp/sox" element={<Navigate to="/compliance-pro/sox" replace />} />

          <Route path="/compliance-pro/*" element={<Navigate to="/compliance-pro" replace />} />

          {/* Vendor Pro */}
          <Route path="/vendor-pro" element={<VendorProHubPage />} />
          <Route path="/vendor-pro/cockpit" element={<VendorProCockpitPage />} />
          <Route path="/vendor-pro/score" element={<VendorProScorePage />} />
          <Route path="/vendor-pro/catalogue" element={<VendorProCataloguePage />} />
          <Route path="/vendor-pro/contracts" element={<VendorProContractsPage />} />
          <Route path="/vendor-pro/costs" element={<VendorProCostsPage />} />
          <Route path="/vendor-pro/due-diligence" element={<VendorProDueDiligencePage />} />
          <Route path="/vendor-pro/performance" element={<VendorProPerformancePage />} />
          <Route path="/vendor-pro/scorecards" element={<VendorProScorecardsPage />} />
          <Route path="/vendor-pro/sla-monitoring" element={<VendorProSlaMonitoringPage />} />
          <Route path="/vendor-pro/concentration" element={<VendorProConcentrationPage />} />
          <Route path="/vendor-pro/renewals" element={<VendorProRenewalsPage />} />
          <Route path="/vendor-pro/clauses" element={<VendorProClausesPage />} />
          <Route path="/vendor-pro/subprocessors" element={<VendorProSubprocessorsPage />} />
          <Route path="/vendor-pro/rgpd" element={<VendorProRgpdPage />} />
          <Route path="/vendor-pro/maturite" element={<VendorProMaturitePage />} />
          <Route path="/vendor-pro/tprm-framework" element={<VendorProTprmFrameworkPage />} />
          <Route path="/vendor-pro/certifications" element={<VendorProCertificationsPage />} />
          <Route path="/vendor-pro/vendor-audit" element={<VendorProVendorAuditPage />} />
          <Route path="/vendor-pro/benchmarks" element={<VendorProBenchmarksPage />} />
          <Route path="/vendor-pro/outils" element={<VendorProOutilsPage />} />
          <Route path="/vendor-pro/report-exec" element={<VendorProReportExecPage />} />
          <Route path="/vendor-pro/audit-trail" element={<VendorProAuditTrailPage />} />
          <Route path="/vendor-pro/integrations" element={<VendorProIntegrationsPage />} />
          <Route path="/vendor-pro/settings" element={<VendorProSettingsPage />} />
          {/* #ATTACK-SURFACE route — must precede the catch-all below, else the
              EASM page is swallowed by the /vendor-pro/* redirect. */}
          <Route path="/vendor-pro/attack-surface" element={<VendorProAttackSurfacePage />} />
          <Route path="/vendor-pro/*" element={<Navigate to="/vendor-pro" replace />} />

          {/* Évaluation & 360° — evaluate consultants/auditors/providers/missions
              + be evaluated by clients. The :id detail route precedes the
              catch-all so the scored form is not swallowed by the redirect. */}
          <Route path="/evaluation-pro" element={<EvaluationHubPage />} />
          <Route path="/evaluation-pro/criteria" element={<EvaluationCriteriaPage />} />
          <Route path="/evaluation-pro/cycles" element={<EvaluationCyclesPage />} />
          <Route path="/evaluation-pro/evaluations" element={<EvaluationEvaluationsPage />} />
          <Route path="/evaluation-pro/evaluations/:id" element={<EvaluationDetailPage />} />
          <Route path="/evaluation-pro/scorecards" element={<EvaluationScorecardsPage />} />
          <Route path="/evaluation-pro/analytics" element={<EvaluationAnalyticsPage />} />
          <Route path="/evaluation-pro/actions" element={<EvaluationActionsPage />} />
          <Route path="/evaluation-pro/*" element={<Navigate to="/evaluation-pro" replace />} />

          {/* Dashboard Pro */}
          <Route path="/dashboard-pro" element={<DashProHubPage />} />
          <Route path="/dashboard-pro/score" element={<DashProScorePage />} />
          <Route path="/dashboard-pro/panorama" element={<DashProPanoramaPage />} />
          <Route path="/dashboard-pro/kpis" element={<DashProKpisPage />} />
          <Route path="/dashboard-pro/score-consolide" element={<DashProScoreConsolidePage />} />
          <Route path="/dashboard-pro/maturite-globale" element={<DashProMaturiteGlobalePage />} />
          <Route path="/dashboard-pro/risque-agrege" element={<DashProRisqueAgregePage />} />
          <Route path="/dashboard-pro/conformite-unifiee" element={<DashProConformiteUnifieePage />} />
          <Route path="/dashboard-pro/tendances" element={<DashProTendancesPage />} />
          <Route path="/dashboard-pro/heatmap" element={<DashProHeatmapPage />} />
          <Route path="/dashboard-pro/correlations" element={<DashProCorrelationsPage />} />
          <Route path="/dashboard-pro/comparaison" element={<DashProComparaisonPage />} />
          <Route path="/dashboard-pro/benchmarks" element={<DashProBenchmarksPage />} />
          <Route path="/dashboard-pro/drill-down" element={<DashProDrillDownPage />} />
          <Route path="/dashboard-pro/c-suite" element={<DashProCSuitePage />} />
          <Route path="/dashboard-pro/tableau-ca" element={<DashProTableauCAPage />} />
          <Route path="/dashboard-pro/rapport-ca" element={<DashProRapportCAPage />} />
          <Route path="/dashboard-pro/alertes" element={<DashProAlertesPage />} />
          <Route path="/dashboard-pro/multi-site" element={<DashProMultiSitePage />} />
          <Route path="/dashboard-pro/deploiement" element={<DashProDeploiementPage />} />
          <Route path="/dashboard-pro/predictive-analytics" element={<DashProPredictiveAnalyticsPage />} />
          <Route path="/dashboard-pro/correlation-engine" element={<DashProCorrelationEnginePage />} />
          <Route path="/dashboard-pro/nl-query" element={<DashProNLQueryPage />} />
          <Route path="/dashboard-pro/auto-benchmark" element={<DashProAutoBenchmarkPage />} />
          <Route path="/dashboard-pro/outils" element={<DashProOutilsPage />} />
          <Route path="/dashboard-pro/audit-trail" element={<DashProAuditTrailPage />} />
          <Route path="/dashboard-pro/integration" element={<DashProIntegrationPage />} />
          <Route path="/dashboard-pro/config" element={<DashProConfigPage />} />
          <Route path="/dashboard-pro/settings" element={<DashProSettingsPage />} />
          {/* Cabinet PSA cockpit — timesheet route registered before cockpit so it is not shadowed */}
          <Route path="/dashboard-pro/psa/timesheet" element={<PsaTimesheetPage />} />
          <Route path="/dashboard-pro/psa" element={<PsaCockpitPage />} />
          <Route path="/dashboard-pro/*" element={<Navigate to="/dashboard-pro" replace />} />

          {/* Ethics Pro */}
          <Route path="/ethics-pro" element={<EthProHubPage />} />
          <Route path="/ethics-pro/cockpit" element={<EthProCockpitPage />} />
          <Route path="/ethics-pro/score" element={<EthProScorePage />} />
          <Route path="/ethics-pro/code-conduite" element={<EthProCodeConduitePage />} />
          <Route path="/ethics-pro/politiques" element={<EthProPolitiquesPage />} />
          <Route path="/ethics-pro/comite-ethique" element={<EthProComiteEthiquePage />} />
          <Route path="/ethics-pro/signalements" element={<EthProSignalementsPage />} />
          <Route path="/ethics-pro/enquetes" element={<EthProEnquetesPage />} />
          <Route path="/ethics-pro/conflit-interet" element={<EthProConflitInteretPage />} />
          <Route path="/ethics-pro/anti-corruption" element={<EthProAntiCorruptionPage />} />
          <Route path="/ethics-pro/sapin-ii" element={<EthProSapinIIPage />} />
          <Route path="/ethics-pro/iso37001" element={<EthProIso37001Page />} />
          <Route path="/ethics-pro/diversite" element={<EthProDiversitePage />} />
          <Route path="/ethics-pro/formation" element={<EthProFormationPage />} />
          <Route path="/ethics-pro/culture" element={<EthProCulturePage />} />
          <Route path="/ethics-pro/maturite" element={<EthProMaturitePage />} />
          <Route path="/ethics-pro/esg" element={<EthProEsgPage />} />
          <Route path="/ethics-pro/alerte" element={<EthProAlertePage />} />
          <Route path="/ethics-pro/certification" element={<EthProCertificationPage />} />
          <Route path="/ethics-pro/frameworks" element={<EthProFrameworksPage />} />
          <Route path="/ethics-pro/outils" element={<EthProOutilsPage />} />
          <Route path="/ethics-pro/report-exec" element={<EthProReportExecPage />} />
          <Route path="/ethics-pro/audit-trail" element={<EthProAuditTrailPage />} />
          <Route path="/ethics-pro/integration" element={<EthProIntegrationPage />} />
          <Route path="/ethics-pro/settings" element={<EthProSettingsPage />} />
          <Route path="/ethics-pro/*" element={<Navigate to="/ethics-pro" replace />} />

          {/* Strategy Pro */}
          <Route path="/strategy-pro" element={<StratProHubPage />} />
          <Route path="/strategy-pro/cockpit" element={<StratProCockpitPage />} />
          <Route path="/strategy-pro/psa" element={<StratProPsaCockpitPage />} />
          <Route path="/strategy-pro/score" element={<StratProScorePage />} />
          <Route path="/strategy-pro/vision" element={<StratProVisionPage />} />
          <Route path="/strategy-pro/objectifs" element={<StratProObjectifsPage />} />
          <Route path="/strategy-pro/key-results" element={<StratProKeyResultsPage />} />
          <Route path="/strategy-pro/okr-dashboard" element={<StratProOkrDashboardPage />} />
          <Route path="/strategy-pro/initiatives" element={<StratProInitiativesPage />} />
          <Route path="/strategy-pro/programs" element={<StratProProgramsPage />} />
          <Route path="/strategy-pro/portfolio" element={<StratProPortfolioPage />} />
          <Route path="/strategy-pro/alignement" element={<StratProAlignementPage />} />
          <Route path="/strategy-pro/bsc-dashboard" element={<StratProBscDashboardPage />} />
          <Route path="/strategy-pro/perspectives" element={<StratProPerspectivesPage />} />
          <Route path="/strategy-pro/strategic-map" element={<StratProStrategicMapPage />} />
          <Route path="/strategy-pro/swot" element={<StratProSwotPage />} />
          <Route path="/strategy-pro/pestel" element={<StratProPestelPage />} />
          <Route path="/strategy-pro/business-case" element={<StratProBusinessCasePage />} />
          <Route path="/strategy-pro/roi" element={<StratProRoiPage />} />
          <Route path="/strategy-pro/governance" element={<StratProGovernancePage />} />
          <Route path="/strategy-pro/frameworks" element={<StratProFrameworksPage />} />
          <Route path="/strategy-pro/pipeline-intelligence" element={<StratProPipelineIntelligencePage />} />
          <Route path="/strategy-pro/revenue-forecast" element={<StratProRevenueForecastPage />} />
          <Route path="/strategy-pro/client-health-score" element={<StratProClientHealthScorePage />} />
          <Route path="/strategy-pro/cross-sell-recommender" element={<StratProCrossSellRecommenderPage />} />
          <Route path="/strategy-pro/board-report-generator" element={<StratProBoardReportGeneratorPage />} />
          <Route path="/strategy-pro/cabinet-benchmark" element={<StratProCabinetBenchmarkPage />} />
          <Route path="/strategy-pro/mission-planner" element={<StratProMissionPlannerPage />} />
          <Route path="/strategy-pro/quality-gate" element={<StratProQualityGatePage />} />
          <Route path="/strategy-pro/deliverable-drafting" element={<StratProDeliverableDraftingPage />} />
          <Route path="/strategy-pro/client-comm-hub" element={<StratProClientCommHubPage />} />
          <Route path="/strategy-pro/time-budget-tracker" element={<StratProTimeBudgetTrackerPage />} />
          <Route path="/strategy-pro/lessons-learned" element={<StratProLessonsLearnedPage />} />
          <Route path="/strategy-pro/outils" element={<StratProOutilsPage />} />
          <Route path="/strategy-pro/report-exec" element={<StratProReportExecPage />} />
          <Route path="/strategy-pro/audit-trail" element={<StratProAuditTrailPage />} />
          <Route path="/strategy-pro/integration" element={<StratProIntegrationPage />} />
          <Route path="/strategy-pro/settings" element={<StratProSettingsPage />} />
          <Route path="/strategy-pro/*" element={<Navigate to="/strategy-pro" replace />} />

          {/* Workspace Pro */}
          <Route path="/workspace-pro" element={<WkspProHubPage />} />
          <Route path="/workspace-pro/chat" element={<WkspProChatPage />} />
          <Route path="/workspace-pro/documents" element={<WkspProDocumentsPage />} />
          <Route path="/workspace-pro/members" element={<WkspProMembersPage />} />
          <Route path="/workspace-pro/permissions" element={<WkspProPermissionsPage />} />
          <Route path="/workspace-pro/invitations" element={<WkspProInvitationsPage />} />
          <Route path="/workspace-pro/agents" element={<WkspProAgentsPage />} />
          <Route path="/workspace-pro/rag-index" element={<WkspProRagIndexPage />} />
          <Route path="/workspace-pro/search" element={<WkspProSearchPage />} />
          <Route path="/workspace-pro/notifications" element={<WkspProNotificationsPage />} />
          <Route path="/workspace-pro/activity" element={<WkspProActivityPage />} />
          <Route path="/workspace-pro/reports" element={<WkspProReportsPage />} />
          <Route path="/workspace-pro/chat-history" element={<WkspProChatHistoryPage />} />
          <Route path="/workspace-pro/profile" element={<WkspProProfilePage />} />
          <Route path="/workspace-pro/themes" element={<WkspProThemesPage />} />
          <Route path="/workspace-pro/quick-start" element={<WkspProQuickStartPage />} />
          <Route path="/workspace-pro/web-access" element={<WkspProWebAccessPage />} />
          <Route path="/workspace-pro/community" element={<WkspProCommunityPage />} />
          <Route path="/workspace-pro/workspace-list" element={<WkspProWorkspaceListPage />} />
          <Route path="/workspace-pro/tools" element={<WkspProToolsPage />} />
          <Route path="/workspace-pro/outils" element={<WkspProOutilsPage />} />
          <Route path="/workspace-pro/module-settings" element={<WkspProModuleSettingsPage />} />
          <Route path="/workspace-pro/audit-trail" element={<WkspProAuditTrailPage />} />
          <Route path="/workspace-pro/integration" element={<WkspProIntegrationPage />} />
          <Route path="/workspace-pro/settings" element={<WkspProSettingsPage />} />
          <Route path="/workspace-pro/*" element={<Navigate to="/workspace-pro" replace />} />

          {/* Framework Engine */}
          <Route path="/framework-engine" element={<FrameworkPortfolioPage />} />
          {/* W4-S29 — orphan framework-engine pages now wired */}
          <Route path="/framework-engine/portfolio" element={<FrameworkPortfolioPage />} />
          <Route path="/framework-engine/catalog" element={<FrameworkCatalogPage />} />
          {/* BE3 fix — these targets fell to the /* catch-all (→ Portfolio); wire the real pages */}
          <Route path="/framework-engine/catalog/:frameworkId" element={<FrameworkAdoptionWizard />} />
          <Route path="/framework-engine/adopt/:frameworkId" element={<FrameworkAdoptionWizard />} />
          <Route path="/framework-engine/wizard/:implementationId" element={<FrameworkAdoptionWizard />} />
          <Route path="/framework-engine/updates" element={<FrameworkUpdateManager />} />
          <Route path="/framework-engine/respond" element={<FrameworkRespondPage />} />
          <Route path="/framework-engine/respond/:implementationId" element={<FrameworkRespondPage />} />
          <Route path="/framework-engine/respond/token/:token" element={<FrameworkRespondPage />} />
          <Route path="/framework-engine/*" element={<FrameworkCatchAll />} />

          {/* Global Views */}
          <Route path="/global/dossiers" element={<GlobalDossiersPage />} />
          <Route path="/global/documents" element={<GlobalDocumentsPage />} />
          <Route path="/global/signatures" element={<GlobalSignaturesPage />} />
          <Route path="/global/calendar" element={<GlobalCalendarPage />} />
          <Route path="/global/messages" element={<GlobalMessagesPage />} />

          {/* Prospection & Offers (PRE_CONTRACT) */}
          <Route path="/prospection" element={<ProspectionPage />} />
          {/* COLLAB-08 — Marketing campaigns + content library */}
          <Route path="/marketing" element={<MarketingPage />} />
          <Route path="/marketing/campaigns" element={<MarketingPage />} />
          <Route path="/marketing/content" element={<MarketingPage />} />
          {/* Profile-MD aliases — Account Manager prospection workflow */}
          <Route path="/prospection/leads" element={<ProspectionPage />} />
          <Route path="/prospection/leads/import" element={<ProspectionPage />} />
          <Route path="/prospection/leads/new" element={<ProspectionPage />} />
          <Route path="/prospection/pipeline" element={<ProspectionPage />} />
          {/* Profile-MD aliases — projects (cross-portal) */}
          <Route path="/projects" element={<Navigate to="/mandats" replace />} />
          <Route path="/projects/list" element={<Navigate to="/mandats" replace />} />
          <Route path="/projects/new" element={<Navigate to="/mandats?create=1" replace />} />
          {/* Privacy aliases — DPO cabinet workflow */}
          <Route path="/privacy/dsr/new" element={<CompProDsrPage />} />
          <Route path="/privacy/registers/treatments" element={<CompProRegisterPage />} />
          <Route path="/privacy/training/program" element={<CompProRegisterPage />} />
          <Route path="/privacy/transfers-international" element={<CompProTransfersPage />} />
          {/* /report/* engagement workflow aliases */}
          <Route path="/report/draft" element={<Navigate to="/audit-pro" replace />} />
          <Route path="/report/publish" element={<Navigate to="/audit-pro" replace />} />
          <Route path="/report/quality-review" element={<Navigate to="/audit-pro" replace />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/offers/*" element={<Navigate to="/offers" replace />} />

          {/* Legal */}
          <Route path="/legal" element={<LegalHubPage />} />
          <Route path="/legal/veille-reglementaire" element={<VeilleReglementairePage />} />
          <Route path="/legal/obligations-contractuelles" element={<ObligationsContractuellesPage />} />
          <Route path="/legal/protection-donnees" element={<ProtectionDonneesPage />} />
          <Route path="/legal/contentieux" element={<ContentieuxPage />} />
          <Route path="/legal/propriete-intellectuelle" element={<ProprieteIntellectuellePage />} />
          <Route path="/legal/gouvernance-corporative" element={<GouvernanceCorporativePage />} />
          <Route path="/legal/droit-travail" element={<DroitTravailPage />} />
          <Route path="/legal/fiscalite" element={<FiscalitePage />} />
          <Route path="/legal/environnement" element={<EnvironnementPage />} />
          {/* Profile-MD aliases (PME incorporée: actes corporatifs) */}
          <Route path="/legal/annual-meeting/new" element={<GouvernanceCorporativePage />} />
          <Route path="/legal/minute-book/new-resolution" element={<GouvernanceCorporativePage />} />
          <Route path="/legal/share-issuances/new" element={<GouvernanceCorporativePage />} />
          <Route path="/legal/share-transfers/new" element={<GouvernanceCorporativePage />} />
          {/* /clients/list alias → ClientsPage handles list view */}
          <Route path="/clients/list" element={<Navigate to="/clients" replace />} />

          {/* Contracts */}
          <Route path="/contracts" element={<ContractsPage />} />
          <Route path="/contracts/*" element={<Navigate to="/contracts" replace />} />

          {/* Relationships */}
          <Route path="/relationships" element={<RelationshipsPage />} />
          <Route path="/relationships/*" element={<Navigate to="/relationships" replace />} />

          {/* Team */}
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/roles" element={<RolesPage />} />
          <Route path="/team/permissions" element={<PermissionsPage />} />
          <Route path="/team/invitations" element={<InvitationsPage />} />
          <Route path="/team/*" element={<Navigate to="/team" replace />} />

          {/* Account — Subscription & Plans */}
          <Route path="/account" element={<Navigate to="/account/subscription" replace />} />
          <Route path="/account/subscription" element={<SubscriptionPage />} />
          <Route path="/account/subscription/cancel" element={<CancelSubscriptionPage />} />
          <Route path="/account/cancel-subscription" element={<CancelSubscriptionPage />} />
          <Route path="/account/plans" element={<PlansPage />} />
          <Route path="/account/credits" element={<CreditsPage />} />
          <Route path="/account/credits/buy" element={<BuyCreditsPage />} />
          <Route path="/account/credits/history" element={<CreditsHistoryPage />} />
          <Route path="/account/credits/transfer" element={<CreditsTransferPage />} />
          <Route path="/account/credits/allocate" element={<CreditsAllocatePage />} />
          <Route path="/account/credits/usage" element={<CreditsUsagePage />} />
          <Route path="/account/features" element={<FeaturesPage />} />
          <Route path="/account/quotas" element={<QuotasPage />} />

          {/* Billing */}
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/invoices/new" element={<BillingPage />} />
          <Route path="/billing/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/billing/payment-methods" element={<PaymentMethodsPage />} />
          <Route path="/billing/tax-receipts" element={<TaxReceiptsPage />} />
          <Route path="/billing/*" element={<BillingPage />} />

          {/* Settings */}
          <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/settings/profile" element={<ProfilePage />} />
          <Route path="/settings/appearance" element={<AppearancePage />} />
          <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
          <Route path="/settings/security" element={<SecurityPage />} />
          <Route path="/settings/developer" element={<DeveloperPage />} />
          <Route path="/settings/privacy" element={<PrivacyPage />} />
          <Route path="/settings/jurisdictions" element={<JurisdictionsPage />} />
          {/* Custom Fields — tenant admin defines dynamic fields per entity-type */}
          <Route path="/settings/custom-fields" element={<CustomFieldsPage />} />

          {/* Cabinet (comptable/fiscaliste) settings — config-driven sections.
              Base route + wildcard so MD deep-links (e.g. /settings/accounting/standards)
              resolve to the section page instead of falling through to NotFound. */}
          {CABINET_SETTINGS_SECTIONS.flatMap((s) => [
            <Route
              key={`cabinet-${s.slug}`}
              path={`/settings/${s.slug}`}
              element={<SettingsSectionPage section={s} />}
            />,
            <Route
              key={`cabinet-${s.slug}-wild`}
              path={`/settings/${s.slug}/*`}
              element={<SettingsSectionPage section={s} />}
            />,
          ])}

          {/* Bottom nav routes */}
          <Route path="/dashboards" element={<Navigate to="/dashboard-pro" replace />} />
          <Route path="/dashboards/*" element={<Navigate to="/dashboard-pro" replace />} />
          <Route path="/regulatory" element={<RegulatoryPage />} />
          <Route path="/assistant" element={<AssistantPage />} />
          {/* AI-07 — RPA / Automation Flows */}
          <Route path="/automation/flows" element={<AutomationFlowsPage />} />
          {/* Subsystem Q — Visual Workflow Builder */}
          <Route path="/workflow-builder" element={<WorkflowBuilderPage />} />
          <Route path="/workflow-builder/new" element={<WorkflowBuilderPage />} />
          <Route path="/workflow-builder/:id" element={<WorkflowBuilderPage />} />

          {/* Business Page Builder — "Ma Page" storefront designer (paid add-on) */}
          <Route path="/ma-page" element={<MaPageBuilderPage />} />
          <Route path="/ma-page/builder" element={<MaPageBuilderPage />} />

          {/* Admin — specific routes BEFORE catch-all */}
          <Route path="/admin/organizations" element={<OrganizationsPage />} />
          <Route path="/admin/organizations/hierarchy" element={<OrganizationHierarchyPage />} />
          <Route path="/admin/organizations/settings" element={<OrganizationSettingsPage />} />
          <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
          <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
          <Route path="/admin/audit" element={<AdminAuditPage />} />
          <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
          {/* AIGOV360 — M13 Continuous-Watch (VEILLE) + config surfaces (before /admin/* catch-all) */}
          <Route path="/admin/aigov/watch-sources" element={<AigovWatchSourcesPage />} />
          <Route path="/admin/aigov/packs" element={<AigovPackEntitlementsPage />} />
          <Route path="/admin/aigov/routing" element={<AigovRoutingRulesPage />} />
          <Route path="/admin/aigov/legal-status" element={<AigovLegalStatusPage />} />
          <Route path="/admin/aigov/pack-changes" element={<AigovPackChangeRequestsPage />} />
          <Route path="/admin/aigov/watch-kpis" element={<AigovWatchKpisPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/*" element={<AdminPage />} />

          <Route path="/support" element={<SupportPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* W4-S29 — Sidebar broken-link stubs (AI Copilot pages, under construction) */}
          {/* AI Copilot Donnees */}
          <Route path="/ai-ia-pro/cost-per-query" element={<CostPerQueryPage />} />
          <Route path="/ai-ia-pro/dataops-pipeline-monitor" element={<DataopsPipelineMonitorPage />} />
          <Route path="/ai-ia-pro/data-product-catalog" element={<DataProductCatalogPage />} />
          <Route path="/ai-ia-pro/schema-evolution-tracker" element={<SchemaEvolutionTrackerPage />} />
          {/* AI Copilot Formateur */}
          <Route path="/change-pro/certification-pathway" element={<CertificationPathwayPage />} />
          <Route path="/change-pro/competency-matrix" element={<CompetencyMatrixPage />} />
          <Route path="/change-pro/learning-analytics" element={<LearningAnalyticsPage />} />
          <Route path="/change-pro/lms-integration" element={<LmsIntegrationPage />} />
          {/* AI Copilot Analyste */}
          <Route path="/dashboard-pro/bayesian-network" element={<BayesianNetworkPage />} />
          <Route path="/dashboard-pro/cohort-analysis" element={<CohortAnalysisPage />} />
          <Route path="/dashboard-pro/monte-carlo" element={<MonteCarloPage />} />
          <Route path="/dashboard-pro/what-if-builder" element={<WhatIfBuilderPage />} />
          {/* Sidebar typos -> redirect to canonical paths */}
          <Route path="/dashboard-pro/csuite" element={<Navigate to="/dashboard-pro/c-suite" replace />} />
          <Route path="/dashboard-pro/risque-agregee" element={<Navigate to="/dashboard-pro/risque-agrege" replace />} />
          <Route path="/strategy-pro/objectives" element={<Navigate to="/strategy-pro/objectifs" replace />} />
          {/* AI Copilot Consultant */}
          <Route path="/governance-pro/cross-reference-checker" element={<CrossReferenceCheckerPage />} />
          <Route path="/governance-pro/interview-transcript-analyzer" element={<InterviewTranscriptAnalyzerPage />} />
          <Route path="/governance-pro/regulatory-database-search" element={<RegulatoryDatabaseSearchPage />} />
          <Route path="/governance-pro/template-intelligence" element={<TemplateIntelligencePage />} />
          <Route path="/governance-pro/working-paper-reviewer" element={<WorkingPaperReviewerPage />} />
          {/* AI Copilot Securite */}
          <Route path="/security-pro/incident-forensics" element={<IncidentForensicsPage />} />
          <Route path="/security-pro/red-team-planner" element={<RedTeamPlannerPage />} />
          <Route path="/security-pro/security-architecture-review" element={<SecurityArchitectureReviewPage />} />
          <Route path="/security-pro/supply-chain-cyber-risk" element={<SupplyChainCyberRiskPage />} />
          {/* AI Copilot Associe + Directeur */}
          <Route path="/strategy-pro/client-expectation-tracker" element={<ClientExpectationTrackerPage />} />
          <Route path="/strategy-pro/client-nps-csat" element={<ClientNpsCsatPage />} />
          <Route path="/strategy-pro/competitive-win-loss" element={<CompetitiveWinLossPage />} />
          <Route path="/strategy-pro/deal-room" element={<DealRoomPage />} />
          <Route path="/strategy-pro/deliverable-quality-scorer" element={<DeliverableQualityScorerPage />} />
          <Route path="/strategy-pro/partner-ecosystem" element={<PartnerEcosystemPage />} />
          <Route path="/strategy-pro/resource-optimizer" element={<ResourceOptimizerPage />} />
          <Route path="/strategy-pro/revenue-attribution" element={<RevenueAttributionPage />} />
          <Route path="/strategy-pro/scope-creep-detector" element={<ScopeCreepDetectorPage />} />
          <Route path="/strategy-pro/sprint-retrospective-ai" element={<SprintRetrospectiveAiPage />} />

          {/* W4-S29 — Test-plan canonical short-alias redirects */}
          <Route path="/sec" element={<Navigate to="/security-pro" replace />} />
          <Route path="/sec/*" element={<Navigate to="/security-pro" replace />} />
          <Route path="/change" element={<Navigate to="/change-pro" replace />} />
          <Route path="/change/*" element={<Navigate to="/change-pro" replace />} />
          <Route path="/gov" element={<Navigate to="/governance-pro" replace />} />
          <Route path="/gov/*" element={<Navigate to="/governance-pro" replace />} />
          <Route path="/dash" element={<Navigate to="/dashboard-pro" replace />} />
          <Route path="/dash/*" element={<Navigate to="/dashboard-pro" replace />} />
          <Route path="/ethics" element={<Navigate to="/ethics-pro" replace />} />
          <Route path="/ethics/*" element={<Navigate to="/ethics-pro" replace />} />
          <Route path="/vendor" element={<Navigate to="/vendor-pro" replace />} />
          <Route path="/vendor/*" element={<Navigate to="/vendor-pro" replace />} />
          <Route path="/bm-pro" element={<Navigate to="/business-model-pro" replace />} />
          <Route path="/bm-pro/*" element={<Navigate to="/business-model-pro" replace />} />
          <Route path="/cabinet/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/clients/create" element={<Navigate to="/clients?create=1" replace />} />

          {/* AI Virtual Experts — Expert Console (strategy/plan/execute) */}
          {aiExpertsRoutes}

          {/* EVT (Events) — create/administer + QR check-in + analytics */}
          {eventsRoutes}

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
